import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Clock,
  RefreshCcw,
  Loader2,
  CheckCircle,
  XCircle,
  PlayCircle,
  Calendar,
  AlertTriangle,
  Bot,
  Download,
  Upload,
  FileText,
  Mail,
  Video,
  Activity,
  TrendingUp,
  BarChart3,
  Square,
  Trash2,
} from "lucide-react";

import NavBackfillRangeCard from "../components/NavBackfillRangeCard";

const Cronjobs = () => {
  const { api } = useAuth();
  const [navLogs, setNavLogs] = useState([]);
  const [navStatus, setNavStatus] = useState(null);  // For NAV breakdown stats
  const [agentLogs, setAgentLogs] = useState([]);
  const [schemeMasterLogs, setSchemeMasterLogs] = useState([]);
  const [amfiCategoryLogs, setAmfiCategoryLogs] = useState([]);
  const [karvyFeedLogs, setKarvyFeedLogs] = useState([]);
  const [lastInitialFetch, setLastInitialFetch] = useState(null);
  const [karvyEmailLogs, setKarvyEmailLogs] = useState([]);
  const [karvyFileLogs, setKarvyFileLogs] = useState([]);
  const [karvyReportStatus, setKarvyReportStatus] = useState([]);
  const [emailHistory, setEmailHistory] = useState({ email_logs: [], file_logs: [], report_status: [] });
  const [emailHistoryLoading, setEmailHistoryLoading] = useState(false);
  const [emailHistoryDays, setEmailHistoryDays] = useState(10);
  const [loading, setLoading] = useState(true);
  const [agentLoading, setAgentLoading] = useState(true);
  const [schemeMasterLoading, setSchemeMasterLoading] = useState(true);
  const [amfiCategoryLoading, setAmfiCategoryLoading] = useState(true);
  const [karvyFeedLoading, setKarvyFeedLoading] = useState(true);
  const [karvyEmailLoading, setKarvyEmailLoading] = useState(true);
  const [runningJob, setRunningJob] = useState(null);
  const [runningAgent, setRunningAgent] = useState(false);
  const [runningSchemeMaster, setRunningSchemeMaster] = useState(false);
  const [schemeMasterJobId, setSchemeMasterJobId] = useState(null);
  const [schemeMasterProgress, setSchemeMasterProgress] = useState(null);
  const [runningAmfiCategory, setRunningAmfiCategory] = useState(false);
  const [amfiCategoryJobId, setAmfiCategoryJobId] = useState(null);
  const [amfiCategoryProgress, setAmfiCategoryProgress] = useState(null);
  const [navJobId, setNavJobId] = useState(null);
  const [navProgress, setNavProgress] = useState(null);
  const [runningKarvyFeed, setRunningKarvyFeed] = useState(false);
  const [karvyFeedJobId, setKarvyFeedJobId] = useState(null);
  const [karvyFeedProgress, setKarvyFeedProgress] = useState(null);
  const [karvyDiagnostics, setKarvyDiagnostics] = useState(null);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const [karvyRecordings, setKarvyRecordings] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  // BSE Client Master scraper — nightly at 3:30 AM IST, saves to Raw Files
  const [bseClientMaster, setBseClientMaster] = useState(null);
  const [runningBseCM, setRunningBseCM] = useState(false);
  const [runningKarvyEmailCheck, setRunningKarvyEmailCheck] = useState(false);
  // Grandfathering card removed 2026-08-30 — the AMFI-backed
  // Backfill Missing NAVs card (from=to=2018-01-31) already lands
  // 31-Jan-2018 NAVs into nav_master reliably.
  const [runningGrandfathering, setRunningGrandfathering] = useState(false);
  const [grandfatheringStatus, setGrandfatheringStatus] = useState(null);
  void runningGrandfathering; void grandfatheringStatus; void setRunningGrandfathering; void setGrandfatheringStatus;
  const [schemeMasterStatus, setSchemeMasterStatus] = useState(null);
  const [amfiCategoryStatus, setAmfiCategoryStatus] = useState(null);
  const [schemeReconciliation, setSchemeReconciliation] = useState(null);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [reconciliationTab, setReconciliationTab] = useState("both");
  const [nextScheduled, setNextScheduled] = useState(null);
  const [agentNextScheduled, setAgentNextScheduled] = useState(null);
  const [agentNextScheduledIST, setAgentNextScheduledIST] = useState(null);
  const [agentSchedule, setAgentSchedule] = useState("Daily at 3:00 AM IST");
  
  // CAMS Feed state
  const [camsFeedLogs, setCamsFeedLogs] = useState([]);
  const [camsFeedLoading, setCamsFeedLoading] = useState(true);
  const [runningCamsFeed, setRunningCamsFeed] = useState(false);
  const [camsFeedStatus, setCamsFeedStatus] = useState(null);
  const [checkingCamsEmails, setCheckingCamsEmails] = useState(false);
  const [camsFeedJobId, setCamsFeedJobId] = useState(null);
  const [camsFeedProgress, setCamsFeedProgress] = useState(null);
  

  // Self-heal mapping state (fills missing description/treatment on transaction_master)
  const [selfHealRunning, setSelfHealRunning] = useState(false);
  const [selfHealStatus, setSelfHealStatus] = useState(null);  // { state, result, started_at, finished_at, error }


  // CAMS WBR9 v4 state
  const [camsWbr9V3JobId, setCamsWbr9V3JobId] = useState(null);
  const [camsWbr9V3Progress, setCamsWbr9V3Progress] = useState(null);
  const [runningCamsWbr9V3, setRunningCamsWbr9V3] = useState(false);
  
  // Email Browser state
  const [availableEmails, setAvailableEmails] = useState({ by_type: { MFSD211: [], MFSD201: [], MFSD203: [] }, all_emails: [] });
  const [emailBrowserLoading, setEmailBrowserLoading] = useState(false);
  const [emailBrowserDays, setEmailBrowserDays] = useState(30);
  const [importingEmail, setImportingEmail] = useState(null);
  const [selectedReportType, setSelectedReportType] = useState("all");
  const [rerunningAll, setRerunningAll] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);  // Last check status
  const [cleaningUpJobs, setCleaningUpJobs] = useState(false);
  
  // Email Reader Scheduler state
  const [emailSchedulerConfig, setEmailSchedulerConfig] = useState({
    karvy_enabled: false,
    karvy_interval: 60, // minutes
    cams_enabled: false,
    cams_interval: 60,
  });
  const [emailTracking, setEmailTracking] = useState([]); // Individual email tracker entries
  const [emailTrackerEntries, setEmailTrackerEntries] = useState([]); // Individual email entries from email_report_tracker
  const [savingScheduler, setSavingScheduler] = useState(false);
  
  // Expected Reports Watchlist
  const [expectedReports, setExpectedReports] = useState([]);
  const [newReport, setNewReport] = useState({ 
    reference_no: '', 
    report_type: 'MFSD211', 
    source: 'KARVY',
    file_type: 'ZIP',
    password: 'Kinntegra@123',
    frequency: 'daily'
  });
  const [addingReport, setAddingReport] = useState(false);
  const [loadingExpectedReports, setLoadingExpectedReports] = useState(false);
  const [selectedTrackerRef, setSelectedTrackerRef] = useState('all');
  const [watchlistTab, setWatchlistTab] = useState('current'); // 'current' | 'historical'

  const fetchNavLogs = useCallback(async () => {
    try {
      const response = await api.get("/cronjobs/nav-update/status");
      setNavLogs(response.data.logs || []);
      setNavStatus(response.data);  // Store full status for breakdown
      // Calculate next scheduled run (2:30 AM IST tomorrow)
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(2, 30, 0, 0); // 2:30 AM IST
      setNextScheduled(tomorrow.toISOString());
    } catch (error) {
      console.error("Failed to fetch NAV logs", error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchAgentLogs = useCallback(async () => {
    try {
      const response = await api.get("/agent/logs");
      setAgentLogs(response.data.logs || []);
      setAgentNextScheduled(response.data.next_scheduled || null);
      setAgentNextScheduledIST(response.data.next_scheduled_ist || null);
      setAgentSchedule(response.data.schedule || "Daily at 3:00 AM IST");
    } catch (error) {
      console.error("Failed to fetch agent logs", error);
    } finally {
      setAgentLoading(false);
    }
  }, [api]);

  const fetchGrandfatheringStatus = useCallback(async () => {
    /* removed 2026-08-30 — see runGrandfatheringJob note */
    return;
  }, []);

  const fetchSchemeMasterStatus = useCallback(async () => {
    try {
      const response = await api.get("/cronjobs/bse-scheme-master/status");
      setSchemeMasterStatus(response.data);
      setSchemeMasterLogs(response.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch scheme master status", error);
    } finally {
      setSchemeMasterLoading(false);
    }
  }, [api]);

  const fetchAmfiCategoryStatus = useCallback(async () => {
    try {
      const response = await api.get("/cronjobs/amfi-category/status");
      setAmfiCategoryStatus(response.data);
      setAmfiCategoryLogs(response.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch AMFI category status", error);
    } finally {
      setAmfiCategoryLoading(false);
    }
  }, [api]);

  // 2026-07-13 — delete a single scheme_master_logs row (Scheme Data /
  // BSE Scheme Master / NAV Master log tables). Orphaned "running" rows
  // that never completed (OOM / container restart mid-run) can be
  // pruned by superuser. Silently refreshes the relevant table on success.
  const deleteSchemeMasterLog = useCallback(
    async (jobId, jobType) => {
      if (!jobId) return;
      if (!window.confirm("Delete this log row? This is irreversible.")) return;
      try {
        await api.delete(`/cronjobs/scheme-master-logs/${encodeURIComponent(jobId)}`);
        toast.success("Log row deleted");
        if (jobType === "NAV_UPDATE") fetchNavLogs();
        else if (jobType === "BSE_SCHEME_MASTER_DOWNLOAD") fetchSchemeMasterStatus();
        else if (jobType === "AMFI_CATEGORY_DOWNLOAD") fetchAmfiCategoryStatus();
        else {
          fetchNavLogs();
          fetchSchemeMasterStatus();
          fetchAmfiCategoryStatus();
        }
      } catch (err) {
        toast.error("Failed to delete log", {
          description: err.response?.data?.detail || "Please try again",
        });
      }
    },
    [api, fetchNavLogs, fetchSchemeMasterStatus, fetchAmfiCategoryStatus],
  );

  const fetchSchemeReconciliation = useCallback(async () => {
    setReconciliationLoading(true);
    try {
      const response = await api.get("/scheme-master/reconciliation");
      setSchemeReconciliation(response.data);
    } catch (error) {
      console.error("Failed to fetch scheme reconciliation", error);
    } finally {
      setReconciliationLoading(false);
    }
  }, [api]);

  // Karvy Feed fetch function
  const fetchKarvyFeedStatus = useCallback(async () => {
    try {
      const response = await api.get("/cronjobs/karvy-feed/status");
      const logs = response.data.logs || [];
      setKarvyFeedLogs(logs);
      
      // Find the last initial fetch
      const lastInitial = logs.find(log => log.is_initial);
      if (lastInitial) {
        setLastInitialFetch(lastInitial.started_at);
      }
      
      // Check if there's a currently running job (e.g., started by scheduler or another session)
      // and we're not already tracking it
      const runningJob = logs.find(log => log.status === "running");
      if (runningJob && !runningKarvyFeed && runningJob.job_id !== karvyFeedJobId) {
        // A job is running but we're not tracking it - update progress display
        setKarvyFeedProgress(runningJob);
        setRunningKarvyFeed(true);
        setKarvyFeedJobId(runningJob.job_id);
      }
    } catch (error) {
      console.error("Failed to fetch Karvy feed logs", error);
    } finally {
      setKarvyFeedLoading(false);
    }
  }, [api, runningKarvyFeed, karvyFeedJobId]);

  // Karvy Email fetch function
  const fetchKarvyEmailStatus = useCallback(async () => {
    try {
      const response = await api.get("/cronjobs/karvy-email/status");
      setKarvyEmailLogs(response.data.email_logs || []);
      setKarvyFileLogs(response.data.file_logs || []);
      setEmailStatus(response.data);  // Store full status including last_checked_at
      // report_status is already returned by the /karvy-email/status payload above
      setKarvyReportStatus(response.data.report_status || []);
    } catch (error) {
      console.error("Failed to fetch Karvy email logs", error);
    } finally {
      setKarvyEmailLoading(false);
    }
  }, [api]);

  // Fetch email history
  const fetchEmailHistory = useCallback(async () => {
    if (!api) return;
    setEmailHistoryLoading(true);
    try {
      const response = await api.get(`/cronjobs/email-history?days=${emailHistoryDays}`);
      setEmailHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch email history", error);
      toast.error("Failed to load email history");
    } finally {
      setEmailHistoryLoading(false);
    }
  }, [api, emailHistoryDays]);

  // Fetch CAMS Feed status
  const fetchCamsFeedStatus = useCallback(async () => {
    try {
      const response = await api.get("/cronjobs/cams-feed/status");
      setCamsFeedLogs(response.data.logs || []);
      setCamsFeedStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch CAMS feed status", error);
    } finally {
      setCamsFeedLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchNavLogs();
    fetchAgentLogs();
    fetchGrandfatheringStatus();
    fetchSchemeMasterStatus();
    fetchAmfiCategoryStatus();
    fetchSchemeReconciliation();
    fetchKarvyFeedStatus();
    fetchKarvyEmailStatus();
    fetchCamsFeedStatus();
    
    // Auto-refresh email status every 60 seconds to show updated "Last Checked"
    const emailRefreshInterval = setInterval(() => {
      fetchKarvyEmailStatus();
    }, 60000);
    
    return () => clearInterval(emailRefreshInterval);
  }, [fetchNavLogs, fetchAgentLogs, fetchGrandfatheringStatus, fetchSchemeMasterStatus, fetchAmfiCategoryStatus, fetchSchemeReconciliation, fetchKarvyFeedStatus, fetchKarvyEmailStatus, fetchCamsFeedStatus]);

  // Poll for running Karvy job status
  useEffect(() => {
    if (!runningKarvyFeed || !karvyFeedJobId) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await api.get(`/cronjobs/karvy-feed/job/${karvyFeedJobId}`);
        const job = response.data;
        
        setKarvyFeedProgress(job);
        
        if (job.status === "completed" || job.status === "partial" || job.status === "failed" || job.status === "cancelled") {
          setRunningKarvyFeed(false);
          setKarvyFeedJobId(null);
          fetchKarvyFeedStatus();
          
          if (job.status === "completed" || job.status === "partial") {
            const successCount = job.reports?.filter(r => r.status === "submitted" || r.status === "already_submitted").length || 0;
            toast.success("Karvy Feed Bot completed!", {
              description: `Submitted ${successCount}/3 reports${job.is_initial ? " (Initial full fetch)" : ""}`,
            });
          } else if (job.status === "failed") {
            toast.error("Karvy Feed Bot failed", {
              description: job.error || "Check logs for details",
            });
          }
        }
      } catch (error) {
        console.error("Failed to poll Karvy Feed status:", error);
      }
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [api, runningKarvyFeed, karvyFeedJobId, fetchKarvyFeedStatus]);

  // Poll for running CAMS job status (similar to Karvy)
  useEffect(() => {
    if (!runningCamsFeed || !camsFeedJobId) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await api.get(`/cronjobs/cams-feed/job/${camsFeedJobId}`);
        const job = response.data;
        
        setCamsFeedProgress(job);
        
        if (job.status === "completed" || job.status === "partial" || job.status === "failed" || job.status === "cancelled") {
          setRunningCamsFeed(false);
          setCamsFeedJobId(null);
          fetchCamsFeedStatus();
          
          if (job.status === "completed" || job.status === "partial") {
            toast.success("CAMS WBR9 Bot completed!", {
              description: job.reference_no ? `Reference: ${job.reference_no}` : "Check logs for details",
            });
          } else {
            toast.error("CAMS WBR9 Bot failed", {
              description: job.error || "An error occurred",
            });
          }
        }
      } catch (pollError) {
        console.error("Error polling CAMS job status:", pollError);
      }
    }, 2000); // Poll every 2 seconds for real-time timeline
    
    return () => clearInterval(pollInterval);
  }, [api, runningCamsFeed, camsFeedJobId, fetchCamsFeedStatus]);

  // Poll for running CAMS WBR9 v4 job status with field-by-field tracking
  useEffect(() => {
    if (!runningCamsWbr9V3 || !camsWbr9V3JobId) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await api.get(`/cronjobs/cams-wbr9-v4/status/${camsWbr9V3JobId}`);
        const job = response.data;
        
        setCamsWbr9V3Progress(job);
        
        // Check if job is complete
        const isComplete = job.status === "completed" || job.status === "failed";
        const hasReference = job.fields_status?.reference_no?.status === "completed" && job.fields_status?.reference_no?.value;
        
        if (isComplete || hasReference) {
          setRunningCamsWbr9V3(false);
          
          if (hasReference) {
            toast.success("CAMS WBR9 v4 completed!", {
              description: `Reference: ${job.fields_status.reference_no.value}`,
            });
            // Auto-close the progress card after 5 seconds when reference is captured
            setTimeout(() => {
              setCamsWbr9V3Progress(null);
              setCamsWbr9V3JobId(null);
            }, 5000);
          } else if (job.status === "failed") {
            toast.error("CAMS WBR9 v4 failed", {
              description: job.error || "Check logs for details",
            });
          }
        }
      } catch (pollError) {
        console.error("Error polling CAMS WBR9 v4 status:", pollError);
      }
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [api, runningCamsWbr9V3, camsWbr9V3JobId]);

  const runSchemeMasterBot = async () => {
    setRunningSchemeMaster(true);
    setSchemeMasterProgress(null);
    try {
      const response = await api.post("/cronjobs/bse-scheme-master/run");
      
      // Bot now runs in background - show info and start polling
      toast.info("BSE Scheme Master Bot started", {
        description: "Download in progress. Please wait...",
      });
      
      // Poll for completion - no timeout, runs until complete
      const jobId = response.data.job_id;
      setSchemeMasterJobId(jobId);
      
      const pollStatus = async () => {
        try {
          const statusRes = await api.get("/cronjobs/bse-scheme-master/status");
          const latestJob = statusRes.data.latest_job;
          
          if (latestJob && latestJob.job_id === jobId) {
            // Track live progress
            if (latestJob.progress_total || latestJob.progress_done != null) {
              setSchemeMasterProgress({
                done: latestJob.progress_done || 0,
                total: latestJob.progress_total || 0,
                phase: latestJob.progress_phase || "writing_bse_raw",
              });
            }
            
            if (latestJob.status === "completed") {
              toast.success("BSE Scheme Master Bot completed!", {
                description: `Processed ${latestJob.records_processed || 0} schemes (${latestJob.inserted || 0} new, ${latestJob.updated || 0} updated)`,
              });
              setRunningSchemeMaster(false);
              setSchemeMasterJobId(null);
              setSchemeMasterProgress(null);
              fetchSchemeMasterStatus();
              return;
            } else if (latestJob.status === "failed") {
              toast.error("BSE Scheme Master Bot failed", {
                description: latestJob.error || "An error occurred",
              });
              setRunningSchemeMaster(false);
              setSchemeMasterJobId(null);
              setSchemeMasterProgress(null);
              fetchSchemeMasterStatus();
              return;
            } else if (latestJob.status === "cancelled") {
              toast.info("BSE Scheme Master Bot cancelled");
              setRunningSchemeMaster(false);
              setSchemeMasterJobId(null);
              setSchemeMasterProgress(null);
              fetchSchemeMasterStatus();
              return;
            }
          }
          
          // Still running, poll every 2 seconds for smooth progress updates
          setTimeout(pollStatus, 2000);
        } catch (pollError) {
          console.error("Error polling status:", pollError);
          // Continue polling even on error
          setTimeout(pollStatus, 3000);
        }
      };
      
      // Start polling after 2 seconds so progress shows quickly
      setTimeout(pollStatus, 2000);
      
    } catch (error) {
      console.error("Failed to start BSE Scheme Master bot:", error);
      toast.error("BSE Scheme Master Bot failed to start", {
        description: error.response?.data?.detail || error.response?.data?.error || "An error occurred",
      });
      setRunningSchemeMaster(false);
      setSchemeMasterJobId(null);
      setSchemeMasterProgress(null);
    }
  };
  
  const stopSchemeMasterBot = async () => {
    if (!schemeMasterJobId) return;
    try {
      const response = await api.post(`/cronjobs/bse-scheme-master/stop/${schemeMasterJobId}`);
      if (response.data.success) {
        toast.info("BSE Scheme Master Bot stopping...", {
          description: "The job has been marked for cancellation",
        });
      } else {
        toast.warning("Could not stop job", { description: response.data.message });
      }
      fetchSchemeMasterStatus();
    } catch (error) {
      console.error("Failed to stop BSE Scheme Master bot:", error);
      toast.error("Failed to stop job", {
        description: error.response?.data?.detail || "An error occurred",
      });
    }
  };

  const runAmfiCategoryBot = async () => {
    setRunningAmfiCategory(true);
    setAmfiCategoryProgress(null);
    try {
      const response = await api.post("/cronjobs/amfi-category/run");
      
      toast.info("Scheme Data Bot started", {
        description: "Processing in progress. Please wait...",
      });
      
      const jobId = response.data.job_id;
      setAmfiCategoryJobId(jobId);
      
      const pollStatus = async () => {
        try {
          const statusRes = await api.get("/cronjobs/amfi-category/status");
          const latestJob = statusRes.data.latest_job;
          
          if (latestJob && latestJob.job_id === jobId) {
            // Track live progress
            if (latestJob.progress_total || latestJob.progress_done != null) {
              setAmfiCategoryProgress({
                done: latestJob.progress_done || 0,
                total: latestJob.progress_total || 0,
                phase: latestJob.progress_phase || "",
              });
            }
            
            if (latestJob.status === "completed") {
              toast.success("Scheme Data Bot completed!", {
                description: `Matched ${latestJob.matched || 0} schemes with categories`,
              });
              setRunningAmfiCategory(false);
              setAmfiCategoryJobId(null);
              setAmfiCategoryProgress(null);
              fetchAmfiCategoryStatus();
              return;
            } else if (latestJob.status === "failed") {
              toast.error("Scheme Data Bot failed", {
                description: latestJob.error || "An error occurred",
              });
              setRunningAmfiCategory(false);
              setAmfiCategoryJobId(null);
              setAmfiCategoryProgress(null);
              fetchAmfiCategoryStatus();
              return;
            } else if (latestJob.status === "cancelled") {
              toast.info("Scheme Data Bot cancelled");
              setRunningAmfiCategory(false);
              setAmfiCategoryJobId(null);
              setAmfiCategoryProgress(null);
              fetchAmfiCategoryStatus();
              return;
            }
          }
          
          setTimeout(pollStatus, 2000);
        } catch (pollError) {
          console.error("Error polling status:", pollError);
          setTimeout(pollStatus, 3000);
        }
      };
      
      setTimeout(pollStatus, 2000);
      
    } catch (error) {
      console.error("Failed to start Scheme Data bot:", error);
      toast.error("Scheme Data Bot failed to start", {
        description: error.response?.data?.detail || error.response?.data?.error || "An error occurred",
      });
      setRunningAmfiCategory(false);
      setAmfiCategoryJobId(null);
      setAmfiCategoryProgress(null);
    }
  };
  
  const stopAmfiCategoryBot = async () => {
    if (!amfiCategoryJobId) return;
    try {
      const response = await api.post(`/cronjobs/amfi-category/stop/${amfiCategoryJobId}`);
      if (response.data.success) {
        toast.info("Scheme Data Bot stopping...", {
          description: "The job has been marked for cancellation",
        });
      } else {
        toast.warning("Could not stop job", { description: response.data.message });
      }
      fetchAmfiCategoryStatus();
    } catch (error) {
      console.error("Failed to stop Scheme Data bot:", error);
      toast.error("Failed to stop job", {
        description: error.response?.data?.detail || "An error occurred",
      });
    }
  };

  const cleanupStuckJobs = async () => {
    setCleaningUpJobs(true);
    try {
      const response = await api.post("/cronjobs/cleanup-stuck-jobs");
      
      if (response.data.success) {
        const totalCleaned = response.data.details.scheme_master_logs + 
                            response.data.details.cams_feed_logs + 
                            response.data.details.karvy_email_logs;
        
        if (totalCleaned > 0) {
          toast.success("Stuck jobs cleaned up!", {
            description: `Marked ${totalCleaned} stuck jobs as failed`,
          });
        } else {
          toast.info("No stuck jobs found", {
            description: "All jobs are running normally",
          });
        }
        
        // Refresh all logs
        fetchSchemeMasterStatus();
        fetchAmfiCategoryStatus();
        fetchKarvyFeedStatus();
        fetchCamsFeedStatus();
      }
    } catch (error) {
      console.error("Failed to cleanup stuck jobs:", error);
      toast.error("Cleanup failed", {
        description: error.response?.data?.detail || "An error occurred",
      });
    } finally {
      setCleaningUpJobs(false);
    }
  };

  const downloadMissingCategoryCSV = async () => {
    try {
      const response = await api.get("/cronjobs/amfi-category/missing/download", {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `schemes_missing_amfi_category_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download started", {
        description: "CSV file with missing category schemes",
      });
    } catch (error) {
      console.error("Failed to download CSV:", error);
      toast.error("Download failed", {
        description: error.response?.data?.detail || "An error occurred",
      });
    }
  };

  // Fetch Karvy diagnostics
  const fetchKarvyDiagnostics = async () => {
    setLoadingDiagnostics(true);
    try {
      const response = await api.get("/cronjobs/karvy-feed/diagnostics");
      setKarvyDiagnostics(response.data);
    } catch (error) {
      console.error("Failed to fetch Karvy diagnostics:", error);
      toast.error("Failed to fetch diagnostics", {
        description: error.response?.data?.detail || "An error occurred",
      });
    } finally {
      setLoadingDiagnostics(false);
    }
  };

  // Fetch Karvy recordings
  const fetchKarvyRecordings = async () => {
    setLoadingRecordings(true);
    try {
      const response = await api.get("/cronjobs/karvy-feed/recordings");
      setKarvyRecordings(response.data.recordings || []);
    } catch (error) {
      console.error("Failed to fetch recordings:", error);
      toast.error("Failed to fetch recordings", {
        description: error.response?.data?.detail || "An error occurred",
      });
    } finally {
      setLoadingRecordings(false);
    }
  };

  // Stop Karvy feed job
  const stopKarvyFeed = async () => {
    if (!karvyFeedJobId) return;
    
    try {
      const response = await api.post(`/cronjobs/karvy-feed/stop/${karvyFeedJobId}`);
      if (response.data.success) {
        toast.info("Karvy Feed Bot stopping...", {
          description: "The job has been marked for cancellation",
        });
        setRunningKarvyFeed(false);
        setKarvyFeedJobId(null);
        setKarvyFeedProgress(null);
        fetchKarvyFeedStatus();
      } else {
        toast.warning("Could not stop job", {
          description: response.data.message,
        });
      }
    } catch (error) {
      console.error("Failed to stop Karvy Feed bot:", error);
      toast.error("Failed to stop job", {
        description: error.response?.data?.detail || "An error occurred",
      });
    }
  };

  const runKarvyFeed = async (isInitial = false) => {
    setRunningKarvyFeed(true);
    setKarvyFeedProgress(null);
    
    try {
      const response = await api.post(`/cronjobs/karvy-feed/run?is_initial=${isInitial}`);
      
      if (response.data.job_id) {
        setKarvyFeedJobId(response.data.job_id);
        toast.info("Karvy Feed Bot started!", {
          description: isInitial ? "Fetching from 01-04-1991 to today" : "Fetching last 15 days",
        });
        
        // Start polling for status
        pollKarvyFeedStatus(response.data.job_id, isInitial);
      }
    } catch (error) {
      console.error("Failed to run Karvy Feed bot:", error);
      toast.error("Karvy Feed Bot failed to start", {
        description: error.response?.data?.detail || error.response?.data?.error || "An error occurred",
      });
      setRunningKarvyFeed(false);
    }
  };

  const pollKarvyFeedStatus = async (jobId, isInitial) => {
    const poll = async () => {
      try {
        const response = await api.get(`/cronjobs/karvy-feed/job/${jobId}`);
        const job = response.data;
        
        setKarvyFeedProgress(job);
        
        if (job.status === "completed" || job.status === "partial" || job.status === "failed") {
          setRunningKarvyFeed(false);
          setKarvyFeedJobId(null);
          fetchKarvyFeedStatus();
          
          if (job.status === "completed" || job.status === "partial") {
            const successCount = job.reports?.filter(r => r.status === "submitted" || r.status === "already_submitted").length || 0;
            toast.success("Karvy Feed Bot completed!", {
              description: `Submitted ${successCount}/3 reports${isInitial ? " (Initial full fetch)" : ""}`,
            });
          } else {
            toast.error("Karvy Feed Bot failed", {
              description: job.error || "Check logs for details",
            });
          }
          return;
        }
        
        // Keep polling until complete - no timeout
        setTimeout(poll, 2000);
      } catch (error) {
        console.error("Failed to poll Karvy Feed status:", error);
        // Continue polling even on error
        setTimeout(poll, 2000);
      }
    };
    
    poll();
  };

  // CAMS Feed Bot
  const runCamsFeedBot = async (isInitial = false) => {
    setRunningCamsFeed(true);
    try {
      const endpoint = isInitial ? "/cronjobs/cams-feed/full-fetch" : "/cronjobs/cams-feed/run";
      const response = await api.post(endpoint);
      
      toast.info(isInitial ? "CAMS Full Fetch started" : "CAMS Feed request started", {
        description: isInitial ? "Fetching WBR9 + WBR2 + WBR2A since 1991. This will take several minutes..." : "Processing request. Please wait...",
      });
      
      const jobId = response.data.job_id;
      
      const pollStatus = async () => {
        try {
          const statusRes = await api.get("/cronjobs/cams-feed/status");
          // Find the job with matching job_id in the logs
          const logs = statusRes.data.logs || [];
          const currentJob = logs.find(log => log.job_id === jobId);
          
          if (currentJob) {
            if (currentJob.status === "completed" || currentJob.status === "partial") {
              toast.success(isInitial ? "CAMS Full Fetch completed!" : "CAMS Feed request completed!", {
                description: currentJob.wbr9_ref ? `WBR9: ${currentJob.wbr9_ref}` : "Check email for report files.",
              });
              setRunningCamsFeed(false);
              fetchCamsFeedStatus();
              return;
            } else if (currentJob.status === "failed") {
              toast.error("CAMS Feed request failed", {
                description: currentJob.error || "An error occurred",
              });
              setRunningCamsFeed(false);
              fetchCamsFeedStatus();
              return;
            }
          }
          
          // Keep polling until complete
          setTimeout(pollStatus, 3000);
        } catch (pollError) {
          console.error("Error polling CAMS status:", pollError);
          setTimeout(pollStatus, 3000);
        }
      };
      
      setTimeout(pollStatus, 5000);
      
    } catch (error) {
      console.error("Failed to run CAMS Feed bot:", error);
      toast.error("CAMS Feed Bot failed to start", {
        description: error.response?.data?.detail || error.response?.data?.error || "An error occurred",
      });
      setRunningCamsFeed(false);
    }
  };

  // CAMS Email Check - Process WBR9 emails
  const checkCamsEmails = async () => {
    setCheckingCamsEmails(true);
    try {
      const response = await api.post("/cronjobs/cams-email/check");
      
      if (response.data.success) {
        toast.success("CAMS Email Check completed!", {
          description: `Processed ${response.data.processed_emails} emails, ${response.data.processed_files} files`,
        });
      } else {
        toast.warning("CAMS Email Check had issues", {
          description: response.data.errors?.slice(0, 2).join(", ") || "Some errors occurred",
        });
      }
      fetchCamsFeedStatus();
    } catch (error) {
      console.error("Failed to check CAMS emails:", error);
      toast.error("CAMS Email Check failed", {
        description: error.response?.data?.detail || "An error occurred",
      });
    } finally {
      setCheckingCamsEmails(false);
    }
  };

  const runKarvyEmailCheck = async () => {
    setRunningKarvyEmailCheck(true);
    
    try {
      const response = await api.post("/cronjobs/karvy-email/check");
      
      if (response.data.success) {
        toast.success("Karvy Email Check completed!", {
          description: `Processed ${response.data.emails_processed} emails, ${response.data.files_processed} files`,
        });
      } else {
        toast.warning("Karvy Email Check had issues", {
          description: response.data.errors?.join(", ") || "Some errors occurred",
        });
      }
      fetchKarvyEmailStatus();
    } catch (error) {
      console.error("Failed to check Karvy emails:", error);
      toast.error("Karvy Email Check failed", {
        description: error.response?.data?.detail || error.response?.data?.error || "An error occurred",
      });
    } finally {
      setRunningKarvyEmailCheck(false);
    }
  };

  // Reprocess existing emails to update "Report Received" status
  const reprocessKarvyEmails = async (days = 7) => {
    setRunningKarvyEmailCheck(true);
    
    try {
      toast.info(`Reprocessing emails from last ${days} days...`);
      const response = await api.post(`/cronjobs/karvy-email/reprocess?days=${days}`);
      
      if (response.data.success) {
        toast.success("Email Reprocess completed!", {
          description: `Checked ${response.data.emails_checked} emails, updated ${response.data.updated} records`,
        });
        fetchKarvyEmailStatus();
        fetchKarvyFeedStatus();
      } else {
        toast.error("Reprocess failed", {
          description: response.data.error || "Unknown error",
        });
      }
    } catch (error) {
      console.error("Failed to reprocess emails:", error);
      toast.error("Reprocess failed", {
        description: error.response?.data?.detail || "An error occurred",
      });
    } finally {
      setRunningKarvyEmailCheck(false);
    }
  };

  // Email Browser functions
  const fetchAvailableEmails = async () => {
    setEmailBrowserLoading(true);
    try {
      const response = await api.get(`/cronjobs/karvy-email/list-available?days_back=${emailBrowserDays}`);
      if (response.data.success) {
        setAvailableEmails(response.data);
        toast.success("Emails fetched", {
          description: `Found ${response.data.total_emails} emails from past ${emailBrowserDays} days`,
        });
      } else {
        toast.error("Failed to fetch emails", {
          description: response.data.error || "Unknown error",
        });
      }
    } catch (error) {
      console.error("Failed to fetch available emails:", error);
      toast.error("Failed to fetch emails", {
        description: error.response?.data?.detail || "An error occurred",
      });
    } finally {
      setEmailBrowserLoading(false);
    }
  };

  // Upload CAMS WBR9 file
  const importSelectedEmail = async (emailData) => {
    setImportingEmail(emailData.email_id);
    try {
      const response = await api.post(
        `/cronjobs/karvy-email/import-selected?email_id=${emailData.email_id}&report_type=${emailData.report_type}&reference_no=${emailData.reference_no || ''}`
      );
      
      if (response.data.success) {
        toast.success("Email imported successfully!", {
          description: `Processed ${response.data.files_processed} files for ${response.data.report_type}`,
        });
        // Refresh the email list to update import status
        fetchAvailableEmails();
      } else {
        toast.error("Import failed", {
          description: response.data.errors?.join(", ") || response.data.error || "Unknown error",
        });
      }
    } catch (error) {
      console.error("Failed to import email:", error);
      toast.error("Import failed", {
        description: error.response?.data?.detail || "An error occurred",
      });
    } finally {
      setImportingEmail(null);
    }
  };

  const rerunAllReadEmails = async () => {
    setRerunningAll(true);
    try {
      const filterParam = selectedReportType !== "all" ? `&report_type=${selectedReportType}` : "";
      const response = await api.post(
        `/cronjobs/karvy-email/rerun-all-read?days_back=${emailBrowserDays}${filterParam}`
      );
      
      if (response.data.success) {
        toast.success("Re-import completed!", {
          description: `Processed ${response.data.processed_count} of ${response.data.total_read_emails} previously imported emails`,
        });
        // Refresh the email list
        fetchAvailableEmails();
      } else {
        toast.error("Re-import failed", {
          description: response.data.error || "Unknown error",
        });
      }
    } catch (error) {
      console.error("Failed to rerun emails:", error);
      toast.error("Re-import failed", {
        description: error.response?.data?.detail || "An error occurred",
      });
    } finally {
      setRerunningAll(false);
    }
  };

  // Expected Reports Watchlist Functions
  const fetchExpectedReports = useCallback(async () => {
    setLoadingExpectedReports(true);
    try {
      const [reportsRes, trackerRes] = await Promise.all([
        api.get("/email-reader/expected-reports"),
        api.get("/email-reader/tracker"),
      ]);
      setExpectedReports(reportsRes.data.reports || []);
      setEmailTrackerEntries(trackerRes.data.entries || []);
    } catch (error) {
      console.error("Failed to fetch expected reports:", error);
    } finally {
      setLoadingExpectedReports(false);
    }
  }, [api]);

  const addExpectedReport = async () => {
    if (!newReport.reference_no.trim()) {
      toast.error("Please enter a reference number");
      return;
    }
    
    setAddingReport(true);
    try {
      const response = await api.post("/email-reader/expected-reports", newReport);
      if (response.data.success) {
        toast.success("Report added to watchlist", {
          description: `Watching for ${newReport.reference_no} → ${newReport.report_type} DB (${newReport.frequency === 'daily' ? 'Daily' : 'One Time'})`,
        });
        // Reset form with all fields
        setNewReport({ 
          reference_no: '', 
          report_type: 'MFSD211', 
          source: 'KARVY',
          file_type: 'ZIP',
          password: 'Kinntegra@123',
          frequency: 'daily'
        });
        fetchExpectedReports();
      }
    } catch (error) {
      console.error("Failed to add expected report:", error);
      toast.error("Failed to add report", {
        description: error.response?.data?.detail || "An error occurred",
      });
    } finally {
      setAddingReport(false);
    }
  };

  const deleteExpectedReport = async (reportId) => {
    try {
      // Find the ref being deleted so we can reset the selected capsule filter if needed
      const deleted = expectedReports.find(r => r.id === reportId);
      await api.delete(`/email-reader/expected-reports/${reportId}`);
      if (deleted && selectedTrackerRef === deleted.reference_no) {
        setSelectedTrackerRef('all');
      }
      toast.success("Report removed from watchlist");
      fetchExpectedReports();
    } catch (error) {
      console.error("Failed to delete expected report:", error);
      toast.error("Failed to remove report");
    }
  };

  const cancelExpectedReport = async (reportId) => {
    try {
      await api.post(`/email-reader/expected-reports/${reportId}/cancel`);
      toast.success("Report cancelled (moved to Historical)");
      fetchExpectedReports();
    } catch (error) {
      console.error("Failed to cancel expected report:", error);
      toast.error("Failed to cancel report");
    }
  };

  const restoreExpectedReport = async (reportId) => {
    try {
      await api.post(`/email-reader/expected-reports/${reportId}/restore`);
      toast.success("Report restored to Current");
      fetchExpectedReports();
    } catch (error) {
      console.error("Failed to restore expected report:", error);
      toast.error("Failed to restore report");
    }
  };

  // Decide whether a watchlist entry belongs to the Historical tab.
  // Historical if: cancelled, OR one-time whose registration date is before today (local date).
  const isHistoricalEntry = (report) => {
    if (!report) return false;
    if (report.status === 'cancelled') return true;
    if (report.frequency === 'one_time' && report.created_at) {
      try {
        const d = new Date(report.created_at);
        const today = new Date();
        const sameDay = d.getFullYear() === today.getFullYear() &&
                        d.getMonth() === today.getMonth() &&
                        d.getDate() === today.getDate();
        return !sameDay;
      } catch {
        return false;
      }
    }
    return false;
  };

  const checkExpectedReports = async () => {
    setEmailBrowserLoading(true);
    try {
      const response = await api.post("/email-reader/check-expected");
      if (response.data.success) {
        toast.success("Email scan started", {
          description: `Scanning ${response.data.checked} references in background. Tracker will update automatically.`,
        });
        // Start polling for updates every 10 seconds
        let pollCount = 0;
        const pollInterval = setInterval(() => {
          fetchExpectedReports();
          pollCount++;
          if (pollCount >= 30) clearInterval(pollInterval); // Stop after 5 minutes
        }, 10000);
      }
    } catch (error) {
      console.error("Failed to check expected reports:", error);
      toast.error("Failed to start email scan", {
        description: error.response?.data?.detail || "Connection error",
      });
    } finally {
      setEmailBrowserLoading(false);
    }
  };

  // Fetch expected reports on mount
  useEffect(() => {
    fetchExpectedReports();
  }, [fetchExpectedReports]);

  const runNavJob = async (jobId = null) => {
    setRunningJob("new");
    setNavProgress(null);
    
    try {
      const response = await api.post("/cronjobs/nav-update/run");
      
      toast.info("NAV Master job started", {
        description: "Fetching NAVs from AMFI + BSE. Please wait...",
      });
      
      const navJobId = response.data.job_id;
      setNavJobId(navJobId);
      
      const pollStatus = async () => {
        try {
          const statusRes = await api.get("/cronjobs/nav-update/status");
          const latestJob = statusRes.data.latest_job;
          
          if (latestJob && latestJob.job_id === navJobId) {
            // Track live progress
            if (latestJob.progress_total || latestJob.progress_done != null) {
              setNavProgress({
                done: latestJob.progress_done || 0,
                total: latestJob.progress_total || 0,
                phase: latestJob.progress_phase || "",
              });
            }
            
            if (latestJob.status === "completed") {
              toast.success("NAV Master update completed!", {
                description: `Updated ${latestJob.schemes_updated || 0} schemes`,
              });
              setRunningJob(null);
              setNavJobId(null);
              setNavProgress(null);
              fetchNavLogs();
              return;
            } else if (latestJob.status === "failed") {
              toast.error("NAV Master update failed", {
                description: latestJob.error || "An error occurred",
              });
              setRunningJob(null);
              setNavJobId(null);
              setNavProgress(null);
              fetchNavLogs();
              return;
            } else if (latestJob.status === "cancelled") {
              toast.info("NAV Master update cancelled");
              setRunningJob(null);
              setNavJobId(null);
              setNavProgress(null);
              fetchNavLogs();
              return;
            }
          }
          
          setTimeout(pollStatus, 2000);
        } catch (pollError) {
          console.error("Error polling status:", pollError);
          setTimeout(pollStatus, 3000);
        }
      };
      
      setTimeout(pollStatus, 2000);
      
    } catch (error) {
      toast.error("Failed to start NAV Master job", {
        description: error.response?.data?.detail || "Please try again",
      });
      setRunningJob(null);
      setNavJobId(null);
      setNavProgress(null);
    }
  };
  
  const stopNavJob = async () => {
    if (!navJobId) return;
    try {
      const response = await api.post(`/cronjobs/nav-update/stop/${navJobId}`);
      if (response.data.success) {
        toast.info("NAV Update stopping...", {
          description: "The job has been marked for cancellation",
        });
      } else {
        toast.warning("Could not stop job", { description: response.data.message });
      }
      fetchNavLogs();
    } catch (error) {
      console.error("Failed to stop NAV update:", error);
      toast.error("Failed to stop job", {
        description: error.response?.data?.detail || "An error occurred",
      });
    }
  };

  const runEmergentAgent = async () => {
    setRunningAgent(true);
    
    try {
      const response = await api.post("/agent/run");
      
      toast.success("Emergent Agent started", {
        description: `Downloading BSE NAV files for 3Y and 5Y ago dates...`,
      });
      
      // Poll for completion
      pollAgentStatus(response.data.job_id);
    } catch (error) {
      toast.error("Failed to start Emergent Agent", {
        description: error.response?.data?.detail || "Please try again",
      });
      setRunningAgent(false);
    }
  };

  const runGrandfatheringJob = async () => {
    /* Grandfathering card removed 2026-08-30 — the AMFI-backed Backfill
     * card (from=to=2018-01-31) already lands 31-Jan-2018 NAVs into
     * nav_master reliably. This stub stays only until the last stray
     * caller is cleaned up. */
    return;
  };

  const pollGrandfatheringStatus = async (jobId) => {
    return;
  };

  const pollAgentStatus = async (jobId) => {
    const maxAttempts = 120; // 4 minutes max (Playwright downloads can take time)
    let attempts = 0;
    
    const poll = async () => {
      try {
        await fetchAgentLogs();
        const job = agentLogs.find(l => l.job_id === jobId) || 
          (await api.get("/agent/logs")).data.logs.find(l => l.job_id === jobId);
        
        if (job && (job.status === "completed" || job.status === "failed")) {
          setRunningAgent(false);
          fetchAgentLogs();
          
          if (job.status === "completed") {
            toast.success("Emergent Agent completed", {
              description: `Downloaded and uploaded ${job.files_uploaded?.length || 0} files`,
            });
          } else {
            toast.error("Emergent Agent failed", {
              description: job.error || "Check logs for details",
            });
          }
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setRunningAgent(false);
          toast.warning("Agent taking longer than expected", {
            description: "Check logs for status",
          });
        }
      } catch (error) {
        setRunningAgent(false);
        fetchAgentLogs();
      }
    };
    
    poll();
  };

  const pollJobStatus = async (jobId) => {
    const maxAttempts = 60; // 2 minutes max
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await api.get(`/cronjobs/nav/status/${jobId}`);
        const status = response.data.status;
        
        if (status === "completed" || status === "failed") {
          setRunningJob(null);
          fetchNavLogs();
          
          if (status === "completed") {
            toast.success("NAV job completed successfully", {
              description: `Processed ${response.data.schemes_updated || 0} schemes`,
            });
          } else {
            toast.error("NAV job failed", {
              description: response.data.error || "Check logs for details",
            });
          }
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setRunningJob(null);
          toast.warning("Job taking longer than expected", {
            description: "Check logs for status",
          });
        }
      } catch (error) {
        setRunningJob(null);
        fetchNavLogs();
      }
    };
    
    poll();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    // Ensure UTC timezone is specified if not present
    let dateWithTZ = dateStr;
    if (!dateStr.includes('+') && !dateStr.includes('Z') && !dateStr.includes('-', 10)) {
      dateWithTZ = dateStr + 'Z';  // Assume UTC if no timezone specified
    }
    const date = new Date(dateWithTZ);
    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };
  
  // Format time only (for timeline steps)
  const formatTimeIST = (dateStr) => {
    if (!dateStr) return "-";
    // Ensure UTC timezone is specified if not present
    let dateWithTZ = dateStr;
    if (!dateStr.includes('+') && !dateStr.includes('Z') && !dateStr.includes('-', 10)) {
      dateWithTZ = dateStr + 'Z';  // Assume UTC if no timezone specified
    }
    const date = new Date(dateWithTZ);
    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    const totalSecs = Math.round(seconds);
    if (totalSecs < 60) return `${totalSecs}s`;
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins}m ${secs}s`;
  };

  // ── Self-heal mapping: one-click fill of missing description/treatment ──
  const fetchSelfHealStatus = useCallback(async () => {
    try {
      const res = await api.get("/transaction-master/remap-status");
      const sh = res.data?.self_heal;
      if (sh) setSelfHealStatus(sh);
      // Auto-reset running flag if DB says job finished
      if (sh && ["done", "error"].includes(sh.state)) {
        setSelfHealRunning(false);
      }
    } catch (e) {
      // Silent — endpoint may not exist on older deploys
    }
  }, [api]);

  const runSelfHeal = async () => {
    if (selfHealRunning) return;
    setSelfHealRunning(true);
    try {
      const res = await api.post("/transaction-master/self-heal-mapping");
      if (res.data?.status === "already_running") {
        toast.info("Self-heal already running. Watching progress…");
      } else {
        toast.success("Self-heal started — this may take a few minutes");
      }
      fetchSelfHealStatus();
    } catch (e) {
      setSelfHealRunning(false);
      toast.error(`Failed to start self-heal: ${e.response?.data?.detail || e.message}`);
    }
  };

  // Poll self-heal status every 5s while running
  useEffect(() => {
    fetchSelfHealStatus();  // initial fetch on mount
    const iv = setInterval(() => {
      if (selfHealRunning || selfHealStatus?.state === "running") {
        fetchSelfHealStatus();
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [selfHealRunning, selfHealStatus?.state, fetchSelfHealStatus]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            Completed
          </Badge>
        );
      case "failed":
        return (
          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      case "running":
        return (
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Running
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            {status}
          </Badge>
        );
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="cronjobs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Cron Jobs</h1>
          <p className="text-gray-500 mt-1">Scheduled tasks and automation</p>
        </div>
        <div className="flex gap-2">
          {/* Cleanup Stuck Jobs button hidden per request.
          <Button 
            variant="outline" 
            onClick={cleanupStuckJobs} 
            disabled={cleaningUpJobs}
            className="text-orange-600 border-orange-300 hover:bg-orange-50"
            data-testid="cleanup-stuck-jobs-btn"
          >
            {cleaningUpJobs ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <AlertTriangle className="w-4 h-4 mr-2" />
            )}
            Cleanup Stuck Jobs
          </Button>
          */}
          <Button variant="outline" onClick={fetchNavLogs} disabled={loading}>
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Mapping Self-Heal — one-click fill of missing description/treatment on transaction_master */}
      <Card data-testid="self-heal-card" className="border-emerald-200 bg-emerald-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="w-5 h-5 text-emerald-600" />
            Mapping Self-Heal
            {selfHealStatus?.state === "running" && (
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />Running
              </Badge>
            )}
            {selfHealStatus?.state === "done" && (
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                <CheckCircle className="w-3 h-3 mr-1" />Last run: done
              </Badge>
            )}
            {selfHealStatus?.state === "error" && (
              <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                <XCircle className="w-3 h-3 mr-1" />Error
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-600">
            One-click backfill of <code className="bg-gray-100 px-1 rounded text-xs">description</code> &amp;{" "}
            <code className="bg-gray-100 px-1 rounded text-xs">treatment</code> on existing CAMS + Karvy transactions.
            Normally run just once — new transactions are auto-mapped at insert.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={runSelfHeal}
              disabled={selfHealRunning || selfHealStatus?.state === "running"}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="self-heal-run-btn"
            >
              {selfHealRunning || selfHealStatus?.state === "running" ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running…</>
              ) : (
                <><PlayCircle className="w-4 h-4 mr-2" />Run Self-Heal</>
              )}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchSelfHealStatus} data-testid="self-heal-refresh-btn">
              <RefreshCcw className="w-4 h-4 mr-1" />Refresh status
            </Button>
          </div>

          {selfHealStatus?.result && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2" data-testid="self-heal-stats">
              <div className="bg-white border rounded p-3">
                <div className="text-xs text-gray-500 uppercase tracking-wide">CAMS healed</div>
                <div className="text-xl font-semibold text-emerald-700">
                  {(selfHealStatus.result.cams_healed ?? 0).toLocaleString()}
                </div>
                <div className="text-xs text-gray-400">
                  of {(selfHealStatus.result.cams_scanned ?? 0).toLocaleString()} scanned
                </div>
              </div>
              <div className="bg-white border rounded p-3">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Karvy healed</div>
                <div className="text-xl font-semibold text-emerald-700">
                  {(selfHealStatus.result.karvy_healed ?? 0).toLocaleString()}
                </div>
                <div className="text-xs text-gray-400">
                  of {(selfHealStatus.result.karvy_scanned ?? 0).toLocaleString()} scanned
                </div>
              </div>
              <div className="bg-white border rounded p-3">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Errors</div>
                <div className={`text-xl font-semibold ${selfHealStatus.result.errors ? "text-red-600" : "text-gray-700"}`}>
                  {selfHealStatus.result.errors ?? 0}
                </div>
              </div>
              <div className="bg-white border rounded p-3">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Finished</div>
                <div className="text-xs font-medium text-gray-700 pt-1">
                  {selfHealStatus.finished_at
                    ? new Date(selfHealStatus.finished_at).toLocaleString()
                    : selfHealStatus.started_at
                      ? `started ${new Date(selfHealStatus.started_at).toLocaleTimeString()}`
                      : "—"}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="amfi-category" className="space-y-4">
        <TabsList>
          <TabsTrigger value="amfi-category" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Scheme Data
          </TabsTrigger>
          <TabsTrigger value="scheme-master" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            BSE Scheme Master
          </TabsTrigger>
          <TabsTrigger value="nav" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            NAV Master
          </TabsTrigger>
          {/* Karvy Feed and CAMS Feed tabs temporarily hidden - switched to manual upload mode */}
          {/* 
          <TabsTrigger value="karvy-feed" className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Karvy Feed
          </TabsTrigger>
          <TabsTrigger value="cams-feed" className="flex items-center gap-2">
            <Bot className="w-4 h-4" />
            CAMS Feed
          </TabsTrigger>
          */}
          <TabsTrigger value="email-manager" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email Reader
          </TabsTrigger>
          <TabsTrigger value="bse-client-master" className="flex items-center gap-2" data-testid="tab-bse-client-master">
            <Download className="w-4 h-4" />
            BSE Client Master
          </TabsTrigger>
        </TabsList>

        <TabsContent value="nav">
          {/* NAV Job Info Card */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Schedule</p>
                    <p className="text-lg font-semibold">Daily at 2:30 AM IST</p>
                    <p className="text-sm text-gray-400 mt-1">Fetches Current + 3Y + 5Y NAVs</p>
                  </div>
                  <Calendar className="w-8 h-8 text-teal-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Schemes</p>
                    <p className="text-lg font-semibold text-teal-600">
                      {navStatus?.total_schemes?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      With NAV: {navStatus?.schemes_with_current_nav?.toLocaleString() || 0}
                    </p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-teal-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Historical NAVs</p>
                    <p className="text-lg font-semibold text-blue-600">
                      3Y: {navStatus?.schemes_with_3y_nav?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      5Y: {navStatus?.schemes_with_5y_nav?.toLocaleString() || 0}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">Manual Run</p>
                    <div className="flex gap-2 mt-2">
                      <Button 
                        onClick={() => runNavJob()}
                        disabled={runningJob !== null}
                        data-testid="nav-run-bot-button"
                      >
                        {runningJob === "new" ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Running...
                          </>
                        ) : (
                          <>
                            <PlayCircle className="w-4 h-4 mr-2" />
                            Run Now
                          </>
                        )}
                      </Button>
                      {runningJob === "new" && navJobId && (
                        <Button
                          onClick={stopNavJob}
                          variant="destructive"
                          data-testid="nav-stop-bot-button"
                        >
                          <Square className="w-4 h-4 mr-2" />
                          Stop
                        </Button>
                      )}
                    </div>
                    {runningJob === "new" && navProgress && navProgress.total > 0 && (
                      <div className="mt-3" data-testid="nav-progress">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>
                            {navProgress.phase === "updating_scheme_data"
                              ? "Updating scheme_data..."
                              : navProgress.phase === "updating_scheme_master"
                              ? "Updating scheme_master..."
                              : `Writing NAV records: ${navProgress.done.toLocaleString()} / ${navProgress.total.toLocaleString()}`}
                          </span>
                          <span className="font-medium text-emerald-700">
                            {Math.min(100, Math.round((navProgress.done / navProgress.total) * 100))}%
                          </span>
                        </div>
                        <Progress
                          value={Math.min(100, (navProgress.done / navProgress.total) * 100)}
                          className="h-2"
                        />
                      </div>
                    )}
                  </div>
                  <PlayCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* NAV Breakdown Stats */}
          <div className="flex flex-wrap gap-3 mb-6">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-50 rounded-lg border border-teal-200">
              <span className="text-sm font-medium text-teal-700">Current NAV:</span>
              <span className="font-bold text-teal-800">{navStatus?.schemes_with_current_nav?.toLocaleString() || 0}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg border border-green-200">
              <span className="text-sm font-medium text-green-700">Growth (with NAV):</span>
              <span className="font-bold text-green-800">{navStatus?.breakdown?.growth_with_nav?.toLocaleString() || 0}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-sm font-medium text-blue-700">IDCW (with NAV):</span>
              <span className="font-bold text-blue-800">{navStatus?.breakdown?.idcw_with_nav?.toLocaleString() || 0}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-lg border border-amber-200">
              <span className="text-sm font-medium text-amber-700">Missing NAV:</span>
              <span className="font-bold text-amber-800">{navStatus?.schemes_without_nav?.toLocaleString() || 0}</span>
            </div>
          </div>

          {/* Logs Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-teal-600" />
                NAV Master Logs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                </div>
              ) : navLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No NAV update logs yet</p>
                  <p className="text-sm">Run the job manually or wait for scheduled execution</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job ID</TableHead>
                      <TableHead>Started At</TableHead>
                      <TableHead>Completed At</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Current NAVs</TableHead>
                      <TableHead>3Y NAVs</TableHead>
                      <TableHead>5Y NAVs</TableHead>
                      <TableHead>Schemes Updated</TableHead>
                      <TableHead>Fallback Retained</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Trigger</TableHead>
                      <TableHead className="w-12 text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {navLogs.map((log) => (
                      <TableRow key={log.job_id || log._id}>
                        <TableCell className="font-mono text-sm">
                          {(log.job_id || log._id)?.substring(0, 8)}...
                        </TableCell>
                        <TableCell>{formatDateTime(log.started_at)}</TableCell>
                        <TableCell>{formatDateTime(log.completed_at)}</TableCell>
                        <TableCell>{formatDuration(log.duration_seconds)}</TableCell>
                        <TableCell>
                          <span className="font-semibold text-teal-600">
                            {log.current_navs?.toLocaleString() || log.schemes_updated?.toLocaleString() || 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold text-blue-600">
                            {log.nav_3y_count?.toLocaleString() || 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold text-purple-600">
                            {log.nav_5y_count?.toLocaleString() || 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold text-green-600">
                            {log.schemes_updated?.toLocaleString() || 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold text-orange-600">
                            {log.fallback_retained?.toLocaleString() || 0}
                          </span>
                        </TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-sm">
                            {log.triggered_by || log.trigger || "manual"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Delete this log row"
                            data-testid={`nav-update-log-delete-${log.job_id || log._id}`}
                            onClick={() =>
                              deleteSchemeMasterLog(log.job_id || log._id, "NAV_UPDATE")
                            }
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Backfill Missing NAVs (date range) */}
          <NavBackfillRangeCard apiUrl={process.env.REACT_APP_BACKEND_URL} />

        </TabsContent>

        {/* Scheme Data Tab (AMFI) - Runs first at 2 AM IST */}
        <TabsContent value="amfi-category">
          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Schedule</p>
                    <p className="text-lg font-semibold">Daily at 2:00 AM IST</p>
                    <p className="text-sm text-gray-400 mt-1">Updates scheme categories from AMFI</p>
                  </div>
                  <Bot className="w-8 h-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Schemes</p>
                    <p className="text-lg font-semibold text-orange-600">
                      {amfiCategoryStatus?.total_scheme_data?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Regular: {amfiCategoryStatus?.breakdown?.regular_schemes?.toLocaleString() || 0} | Direct: {amfiCategoryStatus?.breakdown?.direct_schemes?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-gray-400">
                      Legacy: {amfiCategoryStatus?.breakdown?.legacy_schemes?.toLocaleString() || 0}
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Growth Schemes (Regular)</p>
                    <p className="text-lg font-semibold text-green-600">
                      {amfiCategoryStatus?.breakdown?.regular_growth?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">IDCW Schemes (Regular)</p>
                    <p className="text-lg font-semibold text-blue-600">
                      {amfiCategoryStatus?.breakdown?.regular_idcw?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Payout: {amfiCategoryStatus?.breakdown?.regular_idcw_payout?.toLocaleString() || 0} | Reinvestment: {amfiCategoryStatus?.breakdown?.regular_idcw_reinvestment?.toLocaleString() || 0}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">Manual Run</p>
                    <div className="flex gap-2 mt-2">
                      <Button 
                        onClick={runAmfiCategoryBot}
                        disabled={runningAmfiCategory}
                        size="sm"
                        className="bg-orange-600 hover:bg-orange-700"
                        data-testid="amfi-run-bot-button"
                      >
                        {runningAmfiCategory ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Running...
                          </>
                        ) : (
                          <>
                            <Bot className="w-4 h-4 mr-2" />
                            Run Bot
                          </>
                        )}
                      </Button>
                      {runningAmfiCategory && amfiCategoryJobId && (
                        <Button
                          onClick={stopAmfiCategoryBot}
                          variant="destructive"
                          size="sm"
                          data-testid="amfi-stop-bot-button"
                        >
                          <Square className="w-4 h-4 mr-2" />
                          Stop
                        </Button>
                      )}
                    </div>
                    {runningAmfiCategory && amfiCategoryProgress && amfiCategoryProgress.total > 0 && (
                      <div className="mt-3" data-testid="amfi-progress">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>
                            {amfiCategoryProgress.phase === "building_unified_scheme_master"
                              ? "Building unified scheme_master..."
                              : `Writing ${amfiCategoryProgress.done.toLocaleString()} / ${amfiCategoryProgress.total.toLocaleString()}`}
                          </span>
                          <span className="font-medium text-orange-700">
                            {Math.min(100, Math.round((amfiCategoryProgress.done / amfiCategoryProgress.total) * 100))}%
                          </span>
                        </div>
                        <Progress
                          value={Math.min(100, (amfiCategoryProgress.done / amfiCategoryProgress.total) * 100)}
                          className="h-2"
                        />
                      </div>
                    )}
                  </div>
                  <PlayCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Category & Enrichment Breakdown */}
          <div className="flex flex-wrap gap-3 mb-6">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg border border-green-200">
              <span className="text-sm font-medium text-green-700">With Category:</span>
              <span className="font-bold text-green-800">{amfiCategoryStatus?.schemes_with_category?.toLocaleString() || 0}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-lg border border-amber-200">
              <span className="text-sm font-medium text-amber-700">Missing Category:</span>
              <span className="font-bold text-amber-800">{amfiCategoryStatus?.schemes_without_category?.toLocaleString() || 0}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-lg border border-purple-200">
              <span className="text-sm font-medium text-purple-700">Unique Categories:</span>
              <span className="font-bold text-purple-800">{amfiCategoryStatus?.breakdown?.unique_categories?.toLocaleString() || 0}</span>
            </div>
          </div>

          {/* Scheme Data Logs Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-600" />
                Scheme Data Logs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {amfiCategoryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
                </div>
              ) : !amfiCategoryLogs || amfiCategoryLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No logs yet</p>
                  <p className="text-sm">Run the bot manually or wait for scheduled execution at 2 AM IST</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Started At</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Total Rows</TableHead>
                      <TableHead>Unique ISINs</TableHead>
                      <TableHead>Updated</TableHead>
                      <TableHead>Not Found</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Error</TableHead>
                      <TableHead>Trigger</TableHead>
                      <TableHead className="w-12 text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {amfiCategoryLogs.map((log, idx) => (
                      <TableRow key={idx} className={log.status === 'failed' ? 'bg-red-50' : ''}>
                        <TableCell>{formatDateTime(log.started_at)}</TableCell>
                        <TableCell>{formatDuration(Math.round(log.duration_seconds || 0))}</TableCell>
                        <TableCell>{log.total_rows?.toLocaleString() || "-"}</TableCell>
                        <TableCell>{log.unique_isins?.toLocaleString() || "-"}</TableCell>
                        <TableCell>
                          <span className="font-semibold text-green-600">
                            {log.updated?.toLocaleString() || 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-gray-500">
                            {log.not_found?.toLocaleString() || 0}
                          </span>
                        </TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell className="max-w-xs">
                          {log.error ? (
                            <span className="text-red-600 text-sm truncate" title={log.error}>
                              {log.error}
                            </span>
                          ) : "-"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-sm">
                            {log.triggered_by || "manual"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Delete this log row"
                            data-testid={`amfi-category-log-delete-${log.job_id || idx}`}
                            onClick={() =>
                              deleteSchemeMasterLog(log.job_id, "AMFI_CATEGORY_DOWNLOAD")
                            }
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Scheme Master Summary */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-orange-600" />
                Scheme Master
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reconciliationLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
                </div>
              ) : !schemeReconciliation ? (
                <div className="text-center py-8 text-gray-500">No data</div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-3 mb-4">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg border border-green-200">
                      <span className="text-sm font-medium text-green-700">Scheme Data (AMFI):</span>
                      <span className="font-bold text-green-800">{schemeReconciliation.both?.count?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-lg border border-amber-200">
                      <span className="text-sm font-medium text-amber-700">BSE Only (excluded):</span>
                      <span className="font-bold text-amber-800">{schemeReconciliation.bse_only?.count?.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="max-h-96 overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>ISIN</TableHead>
                          <TableHead>AMFI Code</TableHead>
                          <TableHead>Scheme Name</TableHead>
                          <TableHead>AMC / Plan</TableHead>
                          <TableHead>NAV</TableHead>
                          <TableHead>NAV Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {schemeReconciliation.both?.data?.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-xs">{row.isin}</TableCell>
                            <TableCell>{row.amfi_code}</TableCell>
                            <TableCell className="max-w-xs truncate" title={row.scheme_name}>{row.scheme_name}</TableCell>
                            <TableCell className="text-sm text-gray-500">{row.amc_code} / {row.scheme_plan}</TableCell>
                            <TableCell className="font-semibold text-green-600">{row.current_nav || "-"}</TableCell>
                            <TableCell className="text-sm">{row.current_nav_date || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {schemeReconciliation.both?.count > 500 && (
                      <p className="text-xs text-gray-400 mt-2 text-center">Showing first 500 of {schemeReconciliation.both.count.toLocaleString()}</p>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* BSE Scheme Master Tab - Runs at 2:15 AM IST */}
        <TabsContent value="scheme-master">
          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Schedule</p>
                    <p className="text-lg font-semibold">Daily at 2:15 AM IST</p>
                    <p className="text-sm text-gray-400 mt-1">Downloads all schemes from BSE</p>
                  </div>
                  <Bot className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Schemes</p>
                    <p className="text-lg font-semibold text-purple-600">
                      {schemeMasterStatus?.total_schemes?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Regular: {schemeMasterStatus?.breakdown?.regular_schemes?.toLocaleString() || 0} | Direct: {schemeMasterStatus?.breakdown?.direct_schemes?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-gray-400">
                      {schemeMasterStatus?.breakdown?.unique_amcs || 0} AMCs
                    </p>
                  </div>
                  <Download className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Growth Schemes (Regular)</p>
                    <p className="text-lg font-semibold text-green-600">
                      {schemeMasterStatus?.breakdown?.growth_schemes?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">IDCW Schemes (Regular)</p>
                    <p className="text-lg font-semibold text-blue-600">
                      {schemeMasterStatus?.breakdown?.idcw_schemes?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Payout: {schemeMasterStatus?.breakdown?.idcw_payout?.toLocaleString() || 0} | Reinvestment: {schemeMasterStatus?.breakdown?.idcw_reinvestment?.toLocaleString() || 0}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">Manual Run</p>
                    <div className="flex gap-2 mt-2">
                      <Button 
                        onClick={runSchemeMasterBot}
                        disabled={runningSchemeMaster}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="bse-run-bot-button"
                      >
                        {runningSchemeMaster ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Running...
                          </>
                        ) : (
                          <>
                            <Bot className="w-4 h-4 mr-2" />
                            Run Bot
                          </>
                        )}
                      </Button>
                      {runningSchemeMaster && schemeMasterJobId && (
                        <Button
                          onClick={stopSchemeMasterBot}
                          variant="destructive"
                          data-testid="bse-stop-bot-button"
                        >
                          <Square className="w-4 h-4 mr-2" />
                          Stop
                        </Button>
                      )}
                    </div>
                    {runningSchemeMaster && schemeMasterProgress && schemeMasterProgress.total > 0 && (
                      <div className="mt-3" data-testid="bse-progress">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>
                            {schemeMasterProgress.phase === "building_unified_scheme_master"
                              ? "Building unified scheme_master..."
                              : `Writing ${schemeMasterProgress.done.toLocaleString()} / ${schemeMasterProgress.total.toLocaleString()}`}
                          </span>
                          <span className="font-medium text-purple-700">
                            {Math.min(100, Math.round((schemeMasterProgress.done / schemeMasterProgress.total) * 100))}%
                          </span>
                        </div>
                        <Progress
                          value={Math.min(100, (schemeMasterProgress.done / schemeMasterProgress.total) * 100)}
                          className="h-2"
                        />
                      </div>
                    )}
                  </div>
                  <PlayCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Scheme Master Logs Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-purple-600" />
                BSE Scheme Master Logs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {schemeMasterLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                </div>
              ) : !schemeMasterStatus?.logs || schemeMasterStatus.logs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Download className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No logs yet</p>
                  <p className="text-sm">Run the bot manually or wait for scheduled execution at 2:15 AM IST</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Started At</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Total Lines</TableHead>
                      <TableHead>Processed</TableHead>
                      <TableHead>New / Updated</TableHead>
                      <TableHead>Skipped</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Trigger</TableHead>
                      <TableHead className="w-12 text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schemeMasterStatus.logs.map((log, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{formatDateTime(log.started_at)}</TableCell>
                        <TableCell>{formatDuration(Math.round(log.duration_seconds || 0))}</TableCell>
                        <TableCell>{log.total_lines?.toLocaleString() || "-"}</TableCell>
                        <TableCell>
                          <span className="font-semibold text-green-600">
                            {log.records_processed?.toLocaleString() || 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-blue-600">{log.inserted?.toLocaleString() || 0}</span>
                          {" / "}
                          <span className="text-orange-600">{log.updated?.toLocaleString() || 0}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-gray-500">
                            {log.skipped_no_isin?.toLocaleString() || 0} No ISIN
                          </span>
                        </TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-sm">
                            {log.triggered_by || "manual"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Delete this log row"
                            data-testid={`bse-scheme-master-log-delete-${log.job_id || idx}`}
                            onClick={() =>
                              deleteSchemeMasterLog(log.job_id, "BSE_SCHEME_MASTER_DOWNLOAD")
                            }
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="karvy-feed">
          {/* Karvy Feed Info Card */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Schedule</p>
                    <p className="text-lg font-semibold">Daily at 2:45 AM IST</p>
                    <p className="text-sm text-gray-400 mt-1">MFSD211, MFSD201, MFSD203</p>
                  </div>
                  <Calendar className="w-8 h-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Manual Run (Daily)</p>
                    <div className="flex gap-2 mt-2">
                      <Button 
                        onClick={() => runKarvyFeed(false)}
                        disabled={runningKarvyFeed}
                        variant="outline"
                      >
                        {runningKarvyFeed ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Running...
                          </>
                        ) : (
                          <>
                            <PlayCircle className="w-4 h-4 mr-2" />
                            Run (Last 15 Days)
                          </>
                        )}
                      </Button>
                      {runningKarvyFeed && karvyFeedJobId && (
                        <Button 
                          onClick={stopKarvyFeed}
                          variant="destructive"
                          size="sm"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Stop
                        </Button>
                      )}
                    </div>
                  </div>
                  <PlayCircle className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Initiate Full Fetch</p>
                    <Button 
                      onClick={() => runKarvyFeed(true)}
                      disabled={runningKarvyFeed}
                      className="mt-2"
                      variant="destructive"
                    >
                      {runningKarvyFeed ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Running...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Run (Since 1991)
                        </>
                      )}
                    </Button>
                    {lastInitialFetch && (
                      <p className="text-sm text-gray-500 mt-1">
                        Last: {new Date(lastInitialFetch).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                  <AlertTriangle className="w-8 h-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Debug Recordings</p>
                    <Button 
                      onClick={fetchKarvyRecordings}
                      disabled={loadingRecordings}
                      className="mt-2"
                      variant="outline"
                      size="sm"
                    >
                      {loadingRecordings ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <Video className="w-4 h-4 mr-2" />
                          Load Recordings
                        </>
                      )}
                    </Button>
                    <p className="text-sm text-gray-400 mt-1">View bot session videos</p>
                  </div>
                  <Video className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>

            {/* Diagnostics Card */}
          </div>

          {/* Debug Recordings Panel */}
          {karvyRecordings.length > 0 && (
            <Card className="mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5 text-purple-600" />
                  Bot Session Recordings
                  <Badge variant="outline">{karvyRecordings.length} videos</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {karvyRecordings.slice(0, 10).map((rec, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <Video className="w-5 h-5 text-purple-500" />
                        <div>
                          <p className="font-medium text-sm">{rec.filename}</p>
                          <p className="text-xs text-gray-500">
                            {rec.bot_type.toUpperCase()} • {rec.size_mb} MB • {new Date(rec.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const token = localStorage.getItem('token');
                            window.open(`${process.env.REACT_APP_BACKEND_URL}${rec.download_url}?token=${token}`, '_blank');
                          }}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                  {karvyRecordings.length > 10 && (
                    <p className="text-sm text-gray-500 text-center">
                      Showing 10 of {karvyRecordings.length} recordings
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Real-time Progress Card - Only shown when bot is running */}
          {runningKarvyFeed && karvyFeedProgress && (
            <Card className="mb-6 border-blue-200 bg-blue-50/50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-blue-700">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Karvy Feed Bot Running
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-white text-blue-700 font-mono text-lg px-3">
                      {Math.floor((karvyFeedProgress.elapsed_seconds || 0) / 60)}m {Math.round((karvyFeedProgress.elapsed_seconds || 0) % 60)}s
                    </Badge>
                    {karvyFeedProgress.current_phase && (
                      <Badge variant="default" className="bg-blue-600">
                        {karvyFeedProgress.current_phase.toUpperCase()}
                      </Badge>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Date Range & Current Step */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600">Date Range:</span>
                      <span className="font-medium">{karvyFeedProgress.from_date} → {karvyFeedProgress.to_date}</span>
                      {karvyFeedProgress.is_initial && (
                        <Badge variant="destructive">Initial Full Fetch</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Current:</span>
                      <span className="font-medium text-blue-700">{karvyFeedProgress.current_step}</span>
                    </div>
                  </div>
                  
                  {/* Timeline Steps - Scrollable */}
                  {karvyFeedProgress.timeline && karvyFeedProgress.timeline.length > 0 && (
                    <div className="border rounded-lg bg-white p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Timeline</span>
                        <span className="text-xs text-gray-400">{karvyFeedProgress.timeline.length} steps</span>
                      </div>
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {karvyFeedProgress.timeline.slice().reverse().map((step, idx) => (
                          <div 
                            key={idx} 
                            className={`flex items-center justify-between text-xs py-1 px-2 rounded ${
                              step.status === 'completed' ? 'bg-green-50' :
                              step.status === 'failed' ? 'bg-red-50' :
                              step.status === 'in_progress' ? 'bg-yellow-50' :
                              step.status === 'waiting' ? 'bg-orange-50' :
                              'bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {step.status === 'completed' ? (
                                <CheckCircle className="w-3 h-3 text-green-500" />
                              ) : step.status === 'failed' ? (
                                <XCircle className="w-3 h-3 text-red-500" />
                              ) : step.status === 'in_progress' ? (
                                <Loader2 className="w-3 h-3 animate-spin text-yellow-500" />
                              ) : step.status === 'waiting' ? (
                                <Clock className="w-3 h-3 text-orange-500" />
                              ) : (
                                <Activity className="w-3 h-3 text-gray-400" />
                              )}
                              <span className={`font-medium ${
                                step.status === 'failed' ? 'text-red-700' : 'text-gray-700'
                              }`}>
                                {step.step}
                              </span>
                              <Badge variant="outline" className="text-[10px] px-1 py-0">
                                {step.phase}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 text-gray-500 font-mono">
                              <span className="text-green-600">+{step.duration_sec}s</span>
                              <span className="text-gray-400">|</span>
                              <span className="font-semibold">{Math.floor(step.cumulative_sec / 60)}:{String(Math.round(step.cumulative_sec % 60)).padStart(2, '0')}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Report Status Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    {(karvyFeedProgress.reports || []).map((report, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${
                        report.status === 'submitted' || report.status === 'already_submitted' 
                          ? 'bg-green-50 border-green-200' 
                          : report.status === 'processing' 
                          ? 'bg-yellow-50 border-yellow-200' 
                          : report.status === 'failed'
                          ? 'bg-red-50 border-red-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-center gap-2">
                          {report.status === 'submitted' || report.status === 'already_submitted' ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : report.status === 'processing' ? (
                            <Loader2 className="w-4 h-4 animate-spin text-yellow-500" />
                          ) : report.status === 'failed' ? (
                            <XCircle className="w-4 h-4 text-red-500" />
                          ) : (
                            <Clock className="w-4 h-4 text-gray-400" />
                          )}
                          <span className="font-medium">{report.report_type}</span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {report.status === 'submitted' ? 'Submitted' :
                           report.status === 'already_submitted' ? 'Already Submitted' :
                           report.status === 'processing' ? 'Processing...' :
                           report.status === 'failed' ? 'Failed' :
                           'Pending'}
                        </div>
                        {report.reference_no && (
                          <div className="text-sm font-mono text-gray-500 mt-1">
                            Ref: {report.reference_no}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Karvy Feeds Request Log - Main Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-orange-600" />
                  Karvy Feeds Request Log
                </div>
                <Button
                  onClick={() => reprocessKarvyEmails(7)}
                  disabled={runningKarvyEmailCheck}
                  variant="outline"
                  size="sm"
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                >
                  {runningKarvyEmailCheck ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <RefreshCcw className="w-4 h-4 mr-2" />
                      Update Report Status
                    </>
                  )}
                </Button>
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1">Click "Update Report Status" to sync received timestamps from emails</p>
            </CardHeader>
            <CardContent>
              {karvyFeedLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
                </div>
              ) : karvyFeedLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Upload className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No Karvy feed requests yet</p>
                  <p className="text-sm">Run the bot manually to request reports</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date Range<br/><span className="text-xs text-gray-400">(Trigger / Duration)</span></TableHead>
                      <TableHead>Reports Requested<br/><span className="text-xs text-gray-400">(Reference No)</span></TableHead>
                      <TableHead>Reports Received</TableHead>
                      <TableHead>Reports Read</TableHead>
                      <TableHead>Reports Uploaded</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {karvyFeedLogs.map((log, index) => {
                      const reports = log.reports || [];
                      // Correct order: MFSD211 (Holdings) → MFSD203 (AUM) → MFSD201 (Transactions)
                      const reportTypes = ["MFSD211", "MFSD203", "MFSD201"];
                      
                      // Determine trigger type
                      const getTriggerLabel = () => {
                        if (log.is_initial) return "Initial";
                        const trigger = log.triggered_by || "";
                        if (trigger.includes("scheduler")) return "Bot";
                        if (trigger.includes("manual")) return "Manual";
                        if (trigger.includes("upload")) return "Upload";
                        if (trigger.includes("subscribed")) return "Schedule";
                        return trigger || "Manual";
                      };
                      
                      return (
                        <TableRow key={log.job_id || index}>
                          <TableCell className="font-medium">
                            <div className="text-sm">
                              {log.from_date || "01/04/1991"} to {log.to_date}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                {getTriggerLabel()}
                              </Badge>
                              <span className="text-xs text-gray-500">{formatDuration(log.duration_seconds)}</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {formatDateTime(log.started_at)}
                            </div>
                          </TableCell>
                          {/* Reports Requested - All 3 with Reference Numbers */}
                          <TableCell>
                            <div className="space-y-1">
                              {reportTypes.map(type => {
                                const report = reports.find(r => r.report_type === type);
                                const hasRef = report?.reference_no;
                                return (
                                  <div key={type} className="flex items-center gap-1.5 text-xs">
                                    {hasRef ? (
                                      <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                                    ) : (
                                      <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
                                    )}
                                    <span className={`font-medium ${hasRef ? "text-green-700" : "text-gray-400"}`}>
                                      {type}
                                    </span>
                                    {hasRef && (
                                      <span className="font-mono text-gray-500 text-[10px]">{report.reference_no}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </TableCell>
                          {/* Reports Received - When email arrived */}
                          <TableCell>
                            <div className="space-y-1">
                              {reportTypes.map(type => {
                                const report = reports.find(r => r.report_type === type);
                                const isReceived = report?.received_at;
                                return (
                                  <div key={type} className="flex items-center gap-1.5 text-xs">
                                    {isReceived ? (
                                      <CheckCircle className="w-3 h-3 text-purple-500 flex-shrink-0" />
                                    ) : (
                                      <div className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" />
                                    )}
                                    <span className={isReceived ? "text-purple-700 font-medium" : "text-gray-400"}>
                                      {type}
                                    </span>
                                    {isReceived && (
                                      <span className="font-mono text-gray-400 text-[10px]">
                                        {new Date(report.received_at).toLocaleString('en-IN', {
                                          timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                                        })}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </TableCell>
                          {/* Reports Read - Sequential order */}
                          <TableCell>
                            <div className="space-y-1">
                              {reportTypes.map(type => {
                                const report = reports.find(r => r.report_type === type);
                                const isRead = report?.read_at;
                                return (
                                  <div key={type} className="flex items-center gap-1.5 text-xs">
                                    {isRead ? (
                                      <CheckCircle className="w-3 h-3 text-blue-500 flex-shrink-0" />
                                    ) : (
                                      <div className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" />
                                    )}
                                    <span className={isRead ? "text-blue-700 font-medium" : "text-gray-400"}>
                                      {type}
                                    </span>
                                    {isRead && (
                                      <span className="font-mono text-gray-400 text-[10px]">
                                        {new Date(report.read_at).toLocaleString('en-IN', {
                                          timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                                        })}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </TableCell>
                          {/* Reports Uploaded - Sequential order */}
                          <TableCell>
                            <div className="space-y-1">
                              {reportTypes.map(type => {
                                const report = reports.find(r => r.report_type === type);
                                const isUploaded = report?.uploaded_at;
                                return (
                                  <div key={type} className="flex items-center gap-1.5 text-xs">
                                    {isUploaded ? (
                                      <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                                    ) : (
                                      <div className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" />
                                    )}
                                    <span className={isUploaded ? "text-green-700 font-medium" : "text-gray-400"}>
                                      {type}
                                    </span>
                                    {isUploaded && (
                                      <span className="font-mono text-gray-400 text-[10px]">
                                        {new Date(report.uploaded_at).toLocaleString('en-IN', {
                                          timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                                        })}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CAMS Feed Tab */}
        <TabsContent value="cams-feed">
          {/* CAMS Feed Info Card - Updated for WBR9, WBR2, WBR2A */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Schedule</p>
                    <p className="text-lg font-semibold">Daily at 3:00 AM IST</p>
                    <p className="text-sm text-gray-400 mt-1">WBR9, WBR2, WBR2A Reports</p>
                  </div>
                  <Calendar className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Run WBR9 Only</p>
                    <Button 
                      onClick={async () => {
                        try {
                          setRunningCamsFeed(true);
                          toast.info("Starting CAMS WBR9 Bot with timeline tracking...");
                          const response = await api.post('/cronjobs/cams-feed/run?is_initial=false');
                          if (response.data.job_id) {
                            setCamsFeedJobId(response.data.job_id);
                            setCamsFeedProgress({
                              job_id: response.data.job_id,
                              status: "running",
                              current_step: "initializing",
                              current_phase: "startup",
                              timeline: [],
                              elapsed_seconds: 0
                            });
                          }
                        } catch (error) {
                          toast.error("WBR9 request failed: " + (error.response?.data?.detail || error.message));
                          setRunningCamsFeed(false);
                        }
                      }}
                      disabled={runningCamsFeed}
                      className="mt-2"
                      variant="default"
                    >
                      {runningCamsFeed ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Running...
                        </>
                      ) : (
                        <>
                          <PlayCircle className="w-4 h-4 mr-2" />
                          Run WBR9 (15 Days)
                        </>
                      )}
                    </Button>
                  </div>
                  <Activity className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            {/* WBR9 v4 Bot - State Machine Implementation */}
            <Card className="border-2 border-green-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-semibold">WBR9 v4 Bot (State Machine)</p>
                    <p className="text-xs text-gray-500 mb-2">Follows exact state machine with global checks</p>
                    <Button 
                      onClick={async () => {
                        try {
                          setRunningCamsWbr9V3(true);
                          toast.info("Starting CAMS WBR9 v4 Bot...");
                          const response = await api.post('/cronjobs/cams-wbr9-v4/run?is_initial=false');
                          if (response.data.job_id) {
                            setCamsWbr9V3JobId(response.data.job_id);
                            toast.success(`WBR9 v4 started! Job ID: ${response.data.job_id}`);
                          }
                        } catch (error) {
                          setRunningCamsWbr9V3(false);
                          toast.error("WBR9 v4 failed: " + (error.response?.data?.detail || error.message));
                        }
                      }}
                      disabled={runningCamsWbr9V3}
                      className="mt-2 bg-green-600 hover:bg-green-700"
                    >
                      {runningCamsWbr9V3 ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Running v3...
                        </>
                      ) : (
                        <>
                          <PlayCircle className="w-4 h-4 mr-2" />
                          Run WBR9 v4 (15 Days)
                        </>
                      )}
                    </Button>
                    <Button 
                      onClick={async () => {
                        try {
                          setRunningCamsWbr9V3(true);
                          toast.info("Starting CAMS WBR9 v4 Bot (Full History)...");
                          const response = await api.post('/cronjobs/cams-wbr9-v4/run?is_initial=true');
                          if (response.data.job_id) {
                            setCamsWbr9V3JobId(response.data.job_id);
                            toast.success(`WBR9 v4 (Initial) started! Job ID: ${response.data.job_id}`);
                          }
                        } catch (error) {
                          setRunningCamsWbr9V3(false);
                          toast.error("WBR9 v4 failed: " + (error.response?.data?.detail || error.message));
                        }
                      }}
                      disabled={runningCamsWbr9V3}
                      className="mt-2 ml-2"
                      variant="outline"
                    >
                      {runningCamsWbr9V3 ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Running...
                        </>
                      ) : (
                        <>
                          <PlayCircle className="w-4 h-4 mr-2" />
                          Full History
                        </>
                      )}
                    </Button>
                  </div>
                  <Activity className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Manual Run (Daily)</p>
                    <Button 
                      onClick={async () => {
                        try {
                          setRunningCamsFeed(true);
                          toast.info("Running WBR9, WBR2, WBR2A (last 15 days)...");
                          // Run all three in parallel
                          await Promise.all([
                            api.post('/cronjobs/cams-feed/run?is_initial=false'),
                            api.post('/cronjobs/cams-wbr2/run?days_back=15'),
                            api.post('/cronjobs/cams-wbr2a/run')
                          ]);
                          toast.success("All CAMS requests submitted!");
                        } catch (error) {
                          toast.error("Some requests failed");
                        } finally {
                          setRunningCamsFeed(false);
                          fetchCamsFeedStatus();
                        }
                      }}
                      disabled={runningCamsFeed}
                      className="mt-2"
                      variant="outline"
                    >
                      {runningCamsFeed ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Running...
                        </>
                      ) : (
                        <>
                          <PlayCircle className="w-4 h-4 mr-2" />
                          Run All (15 Days)
                        </>
                      )}
                    </Button>
                  </div>
                  <PlayCircle className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Initiate Full Fetch</p>
                    <Button 
                      onClick={async () => {
                        if (!window.confirm("This will request WBR9, WBR2 since 1991 and WBR2A year-by-year (35+ requests). Continue?")) return;
                        try {
                          setRunningCamsFeed(true);
                          toast.info("Initiating full historical fetch since 1991...");
                          await api.post('/cronjobs/cams-feed/full-fetch');
                          toast.success("Full fetch initiated! Check logs for progress.");
                        } catch (error) {
                          toast.error("Full fetch failed: " + (error.response?.data?.detail || error.message));
                        } finally {
                          setRunningCamsFeed(false);
                          fetchCamsFeedStatus();
                        }
                      }}
                      disabled={runningCamsFeed}
                      className="mt-2"
                      variant="destructive"
                    >
                      {runningCamsFeed ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Running...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Run (Since 1991)
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-400 mt-1">WBR9, WBR2, WBR2A year-wise</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Debug Recordings</p>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      <Button 
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/debug/video/cams/cams_bot_session.webm`, '_blank')}
                        variant="outline"
                        size="sm"
                      >
                        <Video className="w-3 h-3 mr-1" />
                        WBR9
                      </Button>
                      <Button 
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/debug/video/cams/cams_wbr2_latest.webm`, '_blank')}
                        variant="outline"
                        size="sm"
                      >
                        <Video className="w-3 h-3 mr-1" />
                        WBR2
                      </Button>
                      <Button 
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/debug/video/cams/cams_wbr2a_latest.webm`, '_blank')}
                        variant="outline"
                        size="sm"
                      >
                        <Video className="w-3 h-3 mr-1" />
                        WBR2A
                      </Button>
                    </div>
                  </div>
                  <Video className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* CAMS WBR9 Job Progress Card - Real-time Timeline */}
          {(runningCamsFeed || camsFeedProgress) && camsFeedProgress && (
            <Card className="mb-6 border-2 border-purple-200 bg-purple-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-purple-700">
                  {camsFeedProgress.status === "running" ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : camsFeedProgress.status === "completed" ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : camsFeedProgress.status === "failed" ? (
                    <XCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    <Activity className="w-5 h-5" />
                  )}
                  CAMS WBR9 Job Progress
                  <Badge variant={camsFeedProgress.status === "running" ? "default" : camsFeedProgress.status === "completed" ? "success" : "destructive"}>
                    {camsFeedProgress.status?.toUpperCase() || "UNKNOWN"}
                  </Badge>
                  <span className="ml-auto text-sm font-mono text-gray-500">
                    {Math.floor((camsFeedProgress.elapsed_seconds || 0) / 60)}:{String(Math.round((camsFeedProgress.elapsed_seconds || 0) % 60)).padStart(2, '0')}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Step */}
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Phase:</span>
                    <Badge variant="outline" className="text-purple-700">{camsFeedProgress.current_phase || "startup"}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Current:</span>
                    <span className="font-medium text-purple-700">{camsFeedProgress.current_step || "initializing"}</span>
                  </div>
                  {camsFeedProgress.from_date && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Date Range:</span>
                      <span className="font-medium">{camsFeedProgress.from_date} → {camsFeedProgress.to_date}</span>
                    </div>
                  )}
                  {camsFeedProgress.reference_no && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Reference:</span>
                      <span className="font-mono font-bold text-green-700">{camsFeedProgress.reference_no}</span>
                    </div>
                  )}
                </div>
                
                {/* Timeline Steps - Scrollable */}
                {camsFeedProgress.timeline && camsFeedProgress.timeline.length > 0 && (
                  <div className="border rounded-lg bg-white p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Timeline</span>
                      <span className="text-xs text-gray-400">{camsFeedProgress.timeline.length} steps</span>
                    </div>
                    <div className="max-h-64 overflow-y-auto space-y-1">
                      {camsFeedProgress.timeline.slice().reverse().map((step, idx) => (
                        <div 
                          key={idx} 
                          className={`flex items-center justify-between text-xs py-1.5 px-2 rounded ${
                            step.status === 'completed' ? 'bg-green-50' :
                            step.status === 'failed' ? 'bg-red-50' :
                            step.status === 'in_progress' ? 'bg-yellow-50' :
                            step.status === 'waiting' ? 'bg-orange-50' :
                            step.status === 'warning' ? 'bg-amber-50' :
                            'bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2 flex-1">
                            {step.status === 'completed' ? (
                              <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                            ) : step.status === 'failed' ? (
                              <XCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
                            ) : step.status === 'in_progress' ? (
                              <Loader2 className="w-3 h-3 animate-spin text-yellow-500 flex-shrink-0" />
                            ) : step.status === 'waiting' ? (
                              <Clock className="w-3 h-3 text-orange-500 flex-shrink-0" />
                            ) : step.status === 'warning' ? (
                              <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                            ) : (
                              <Activity className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            )}
                            <span className={`font-medium ${
                              step.status === 'failed' ? 'text-red-700' : 
                              step.status === 'warning' ? 'text-amber-700' : 
                              'text-gray-700'
                            }`}>
                              {step.step}
                            </span>
                            <Badge variant="outline" className="text-[10px] px-1 py-0 flex-shrink-0">
                              {step.phase}
                            </Badge>
                            {step.value && (
                              <span className="text-gray-500 font-mono text-[10px] truncate max-w-[150px]" title={step.value}>
                                ({step.value})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-gray-500 font-mono flex-shrink-0 ml-2">
                            <span className="text-green-600">+{step.duration_sec}s</span>
                            <span className="text-gray-400">|</span>
                            <span className="font-semibold">{Math.floor(step.cumulative_sec / 60)}:{String(Math.round(step.cumulative_sec % 60)).padStart(2, '0')}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Error Display */}
                {camsFeedProgress.error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700 font-medium">Error:</p>
                    <p className="text-sm text-red-600">{camsFeedProgress.error}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* CAMS WBR9 v4 Job Progress Card - Field-by-Field Status */}
          {(runningCamsWbr9V3 || camsWbr9V3Progress) && camsWbr9V3Progress && (
            <Card className="mb-6 border-2 border-green-200 bg-green-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-green-700">
                  {camsWbr9V3Progress.current_status === "in_progress" ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : camsWbr9V3Progress.fields_status?.reference_no?.status === "completed" ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : camsWbr9V3Progress.current_status === "failed" ? (
                    <XCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    <Activity className="w-5 h-5" />
                  )}
                  CAMS WBR9 v4 - Field Status
                  <Badge variant={runningCamsWbr9V3 ? "default" : camsWbr9V3Progress.fields_status?.reference_no?.status === "completed" ? "success" : "secondary"}>
                    Step {camsWbr9V3Progress.current_step || 0}
                  </Badge>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="ml-auto"
                    onClick={() => {
                      setCamsWbr9V3Progress(null);
                      setCamsWbr9V3JobId(null);
                      setRunningCamsWbr9V3(false);
                    }}
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Step Description */}
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Current Action:</span> {camsWbr9V3Progress.current_description || "Initializing..."}
                  </p>
                </div>

                {/* Field-by-Field Status Grid */}
                {camsWbr9V3Progress.fields_status && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(camsWbr9V3Progress.fields_status).map(([field, data]) => (
                      <div 
                        key={field}
                        className={`p-3 rounded-lg border ${
                          data.status === 'completed' ? 'bg-green-50 border-green-200' :
                          data.status === 'in_progress' ? 'bg-yellow-50 border-yellow-200' :
                          data.status === 'failed' ? 'bg-red-50 border-red-200' :
                          'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          {data.status === 'completed' ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : data.status === 'in_progress' ? (
                            <Loader2 className="w-4 h-4 animate-spin text-yellow-500" />
                          ) : data.status === 'failed' ? (
                            <XCircle className="w-4 h-4 text-red-500" />
                          ) : (
                            <Clock className="w-4 h-4 text-gray-400" />
                          )}
                          <span className="text-xs font-semibold uppercase text-gray-600">
                            {field.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <p className={`text-sm font-mono truncate ${
                          data.status === 'completed' ? 'text-green-700' :
                          data.status === 'failed' ? 'text-red-700' :
                          'text-gray-500'
                        }`} title={data.value || 'Pending'}>
                          {data.value || '—'}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reference Number Highlight */}
                {camsWbr9V3Progress.fields_status?.reference_no?.status === "completed" && (
                  <div className="p-4 bg-green-100 rounded-lg border-2 border-green-300 text-center">
                    <p className="text-sm text-green-600 font-medium mb-1">Reference Number Generated!</p>
                    <p className="text-2xl font-bold font-mono text-green-800">
                      {camsWbr9V3Progress.fields_status.reference_no.value}
                    </p>
                    <p className="text-xs text-green-600 mt-2">This card will auto-close in 5 seconds...</p>
                  </div>
                )}

                {/* Timeline (collapsed by default) */}
                {camsWbr9V3Progress.timeline && camsWbr9V3Progress.timeline.length > 0 && (
                  <details className="border rounded-lg bg-white">
                    <summary className="p-3 cursor-pointer text-sm font-medium text-gray-600 hover:bg-gray-50">
                      View Timeline ({camsWbr9V3Progress.timeline.length} steps)
                    </summary>
                    <div className="p-3 max-h-48 overflow-y-auto space-y-1 border-t">
                      {camsWbr9V3Progress.timeline.slice().reverse().map((step, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center justify-between text-xs py-1.5 px-2 rounded ${
                            step.status === 'completed' ? 'bg-green-50' :
                            step.status === 'failed' ? 'bg-red-50' :
                            step.status === 'in_progress' ? 'bg-yellow-50' :
                            'bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {step.status === 'completed' ? (
                              <CheckCircle className="w-3 h-3 text-green-500" />
                            ) : step.status === 'failed' ? (
                              <XCircle className="w-3 h-3 text-red-500" />
                            ) : step.status === 'in_progress' ? (
                              <Loader2 className="w-3 h-3 animate-spin text-yellow-500" />
                            ) : (
                              <Activity className="w-3 h-3 text-gray-400" />
                            )}
                            <span className="font-medium">Step {step.step}</span>
                            <span className="text-gray-600">{step.description}</span>
                          </div>
                          <span className="text-gray-500 font-mono">{step.elapsed_sec}s</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}

                {/* Error Display */}
                {camsWbr9V3Progress.error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700 font-medium">Error:</p>
                    <p className="text-sm text-red-600">{camsWbr9V3Progress.error}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* CAMS Feeds Request Log - Main Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-purple-600" />
                CAMS Feeds Request Log
              </CardTitle>
            </CardHeader>
            <CardContent>
              {camsFeedLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                </div>
              ) : camsFeedLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Upload className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No CAMS feed requests yet</p>
                  <p className="text-sm">Run the bot manually to request reports</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date Range<br/><span className="text-xs text-gray-400">(Trigger / Duration)</span></TableHead>
                      <TableHead>Report Requested<br/><span className="text-xs text-gray-400">(Reference No)</span></TableHead>
                      <TableHead>Report Received</TableHead>
                      <TableHead>Report Read</TableHead>
                      <TableHead>Report Uploaded</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {camsFeedLogs.map((log, index) => {
                      // Get report types for CAMS
                      const camsReportTypes = ['WBR9', 'WBR2', 'WBR2A'];
                      const reports = log.reports || [];
                      
                      // Determine trigger type
                      const getTriggerLabel = () => {
                        if (log.is_initial || log.job_type === 'CAMS_FULL_FETCH') return "Full Fetch";
                        const trigger = log.triggered_by || "";
                        if (trigger.includes("scheduler")) return "Bot";
                        if (trigger.includes("manual")) return "Manual";
                        return trigger || "Manual";
                      };
                      
                      // Get duration
                      const duration = log.elapsed_seconds || log.duration_seconds || 0;
                      
                      return (
                        <TableRow key={log.job_id || index}>
                          <TableCell className="font-medium">
                            <div className="text-sm">
                              {log.from_date || "Since Inception"} to {log.to_date || "Today"}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                                {getTriggerLabel()}
                              </Badge>
                              <span className="text-xs text-gray-500">{formatDuration(duration)}</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {formatDateTime(log.started_at)}
                            </div>
                          </TableCell>
                          {/* Reports Requested */}
                          <TableCell>
                            <div className="space-y-1">
                              {camsReportTypes.map(type => {
                                const report = reports.find(r => r.report_type === type);
                                // For WBR2A with multiple years, show progress
                                let displayRef = report?.reference_no;
                                if (type === 'WBR2A' && report?.years_completed) {
                                  displayRef = `${report.years_completed}/${report.total_years || '?'} years`;
                                }
                                const hasRef = report?.status === 'submitted' || !!report?.reference_no;
                                const isProcessing = report?.status === 'processing';
                                const isPending = !report || report?.status === 'pending';
                                
                                return (
                                  <div key={type} className="flex items-center gap-1.5 text-xs">
                                    {hasRef ? (
                                      <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                                    ) : isProcessing ? (
                                      <Loader2 className="w-3 h-3 text-yellow-500 animate-spin flex-shrink-0" />
                                    ) : isPending ? (
                                      <div className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" />
                                    ) : (
                                      <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
                                    )}
                                    <span className={`font-medium ${
                                      hasRef ? "text-green-700" : 
                                      isProcessing ? "text-yellow-700" : 
                                      "text-gray-400"
                                    }`}>
                                      {type}
                                    </span>
                                    {displayRef && (
                                      <span className="font-mono text-gray-500 text-[10px]">{displayRef}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </TableCell>
                          {/* Reports Received */}
                          <TableCell>
                            <div className="space-y-1">
                              {camsReportTypes.map(type => {
                                const report = reports.find(r => r.report_type === type);
                                const isReceived = report?.received_at;
                                return (
                                  <div key={type} className="flex items-center gap-1.5 text-xs">
                                    {isReceived ? (
                                      <CheckCircle className="w-3 h-3 text-purple-500 flex-shrink-0" />
                                    ) : (
                                      <div className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" />
                                    )}
                                    <span className={isReceived ? "text-purple-700 font-medium" : "text-gray-400"}>
                                      {type}
                                    </span>
                                    {isReceived && (
                                      <span className="font-mono text-gray-400 text-[10px]">
                                        {new Date(report.received_at).toLocaleString('en-IN', {
                                          timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                                        })}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </TableCell>
                          {/* Reports Read */}
                          <TableCell>
                            <div className="space-y-1">
                              {camsReportTypes.map(type => {
                                const report = reports.find(r => r.report_type === type);
                                const isRead = report?.read_at;
                                return (
                                  <div key={type} className="flex items-center gap-1.5 text-xs">
                                    {isRead ? (
                                      <CheckCircle className="w-3 h-3 text-blue-500 flex-shrink-0" />
                                    ) : (
                                      <div className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" />
                                    )}
                                    <span className={isRead ? "text-blue-700 font-medium" : "text-gray-400"}>
                                      {type}
                                    </span>
                                    {isRead && (
                                      <span className="font-mono text-gray-400 text-[10px]">
                                        {new Date(report.read_at).toLocaleString('en-IN', {
                                          timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                                        })}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </TableCell>
                          {/* Reports Uploaded */}
                          <TableCell>
                            <div className="space-y-1">
                              {camsReportTypes.map(type => {
                                const report = reports.find(r => r.report_type === type);
                                const isUploaded = report?.uploaded_at;
                                return (
                                  <div key={type} className="flex items-center gap-1.5 text-xs">
                                    {isUploaded ? (
                                      <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                                    ) : (
                                      <div className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" />
                                    )}
                                    <span className={isUploaded ? "text-green-700 font-medium" : "text-gray-400"}>
                                      {type}
                                    </span>
                                    {isUploaded && (
                                      <span className="font-mono text-gray-400 text-[10px]">
                                        {new Date(report.uploaded_at).toLocaleString('en-IN', {
                                          timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                                        })}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Reader Tab - Report Watchlist with Status Tracking */}
        <TabsContent value="email-manager">
          {/* Add Expected Report Section */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Add Expected Report
                <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700">
                  Watchlist
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 items-end">
                {/* Source Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Source</label>
                  <select
                    value={newReport.source}
                    onChange={(e) => setNewReport(prev => ({ 
                      ...prev, 
                      source: e.target.value
                    }))}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="KARVY">KARVY</option>
                    <option value="CAMS">CAMS</option>
                  </select>
                </div>
                
                {/* Add to DB Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Add to DB</label>
                  <select
                    data-testid="add-to-db-select"
                    value={newReport.report_type}
                    onChange={(e) => setNewReport(prev => ({ ...prev, report_type: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="MFSD211">MFSD211</option>
                    <option value="MFSD203">MFSD203</option>
                    <option value="MFSD201">MFSD201</option>
                    <option value="WBR9">WBR9</option>
                    <option value="WBR9D">WBR9D</option>
                    <option value="WBR2">WBR2</option>
                    <option value="WBR2A">WBR2A</option>
                  </select>
                </div>
                
                {/* Reference Number Input */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Reference Number</label>
                  <input
                    type="text"
                    value={newReport.reference_no}
                    onChange={(e) => setNewReport(prev => ({ ...prev, reference_no: e.target.value }))}
                    placeholder="Enter reference no..."
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>
                
                {/* File Type Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">File Type</label>
                  <select
                    value={newReport.file_type}
                    onChange={(e) => setNewReport(prev => ({ ...prev, file_type: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="ZIP">ZIP (Password Protected)</option>
                    <option value="TXT">TXT (Plain Text)</option>
                    <option value="XLSX">Excel (.xlsx)</option>
                    <option value="XLS">Excel (.xls)</option>
                    <option value="DBF">DBF (Database)</option>
                  </select>
                </div>
                
                {/* Password Input */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Password</label>
                  <input
                    type="text"
                    value={newReport.password}
                    onChange={(e) => setNewReport(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="File password..."
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    disabled={newReport.file_type !== 'ZIP'}
                  />
                </div>
                
                {/* Frequency Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Frequency</label>
                  <select
                    value={newReport.frequency}
                    onChange={(e) => setNewReport(prev => ({ ...prev, frequency: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="daily">Daily</option>
                    <option value="one_time">One Time</option>
                  </select>
                </div>
                
                {/* Add Button */}
                <div className="flex items-end">
                  <Button 
                    onClick={addExpectedReport}
                    disabled={addingReport || !newReport.reference_no.trim()}
                    className="w-full"
                  >
                    {addingReport ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Add
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <strong>How it works:</strong> Add reference numbers for reports you're expecting. The system scans Gmail every 5 minutes for these references. Each email found is tracked individually with its received date. When the ZIP is downloaded and data is written to the respective DB, it's marked as Read.
              </div>
            </CardContent>
          </Card>
          
          {/* Watchlist — configured reference numbers */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm" data-testid="watchlist-title">
                <FileText className="w-4 h-4" />
                Watchlist ({expectedReports.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const currentEntries = expectedReports.filter(r => !isHistoricalEntry(r));
                const historicalEntries = expectedReports.filter(isHistoricalEntry);
                const rowsToShow = watchlistTab === 'current' ? currentEntries : historicalEntries;
                return (
                  <>
                    {/* Tabs */}
                    <div className="flex items-center gap-2 mb-4 border-b">
                      <button
                        data-testid="watchlist-tab-current"
                        onClick={() => setWatchlistTab('current')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${
                          watchlistTab === 'current'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Current ({currentEntries.length})
                      </button>
                      <button
                        data-testid="watchlist-tab-historical"
                        onClick={() => setWatchlistTab('historical')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${
                          watchlistTab === 'historical'
                            ? 'border-gray-700 text-gray-700'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Historical ({historicalEntries.length})
                      </button>
                    </div>
                    {rowsToShow.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead>DB</TableHead>
                      <TableHead>Reference No.</TableHead>
                      <TableHead>Frequency</TableHead>
                      <TableHead>Registered</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rowsToShow.map((report, index) => (
                      <TableRow key={report.id || index}>
                        <TableCell>
                          <Badge variant="outline" className={report.source === 'KARVY' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}>
                            {report.source}
                          </Badge>
                        </TableCell>
                        <TableCell><Badge variant="secondary">{report.report_type}</Badge></TableCell>
                        <TableCell className="font-mono text-sm font-semibold">{report.reference_no}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={report.frequency === 'daily' ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-700'}>
                            {report.frequency === 'daily' ? 'Daily' : 'One Time'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-gray-600 font-mono" data-testid={`watchlist-registered-${report.reference_no}`}>
                          {report.created_at ? (() => {
                            try {
                              const d = new Date(report.created_at);
                              const dd = String(d.getDate()).padStart(2, '0');
                              const mm = String(d.getMonth() + 1).padStart(2, '0');
                              const yyyy = d.getFullYear();
                              const hh = String(d.getHours()).padStart(2, '0');
                              const mi = String(d.getMinutes()).padStart(2, '0');
                              return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
                            } catch {
                              return '-';
                            }
                          })() : '-'}
                        </TableCell>
                        <TableCell>
                          {report.status === 'read' ? (
                            <Badge className="bg-green-100 text-green-700">Read (last: {report.records_count || 0} recs)</Badge>
                          ) : report.status === 'download_failed' ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className="bg-red-100 text-red-700">Download Failed</Badge>
                              {report.cams_download_url && (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="text-xs h-7 px-2 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                                    data-testid={`cams-download-${report.reference_no}`}
                                    onClick={() => window.open(report.cams_download_url, '_blank')}
                                  >
                                    1. Download ZIP
                                  </Button>
                                  <label className="inline-flex items-center gap-1 text-xs h-7 px-2 rounded-md border cursor-pointer bg-green-50 text-green-700 border-green-200 hover:bg-green-100">
                                    2. Upload & Process
                                    <input 
                                      type="file" 
                                      accept=".zip"
                                      className="hidden"
                                      data-testid={`proxy-upload-${report.reference_no}`}
                                      onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;
                                        try {
                                          toast.info(`Uploading ${file.name}...`);
                                          const formData = new FormData();
                                          formData.append('file', file);
                                          formData.append('reference_no', report.reference_no);
                                          const uploadResp = await api.post('/email-reader/proxy-upload', formData, {
                                            headers: { 'Content-Type': 'multipart/form-data' }
                                          });
                                          if (uploadResp.data.success) {
                                            toast.success(`${report.reference_no} received! Processing ${report.report_type} in background. Refresh in a few seconds to see status.`, { duration: 6000 });
                                            setTimeout(() => fetchExpectedReports(), 5000);
                                          } else {
                                            toast.error(uploadResp.data.error || 'Processing failed');
                                          }
                                        } catch (err) {
                                          toast.error(`Upload failed: ${err.message}`);
                                        }
                                        e.target.value = '';
                                      }}
                                    />
                                  </label>
                                </>
                              )}
                            </div>
                          ) : report.status === 'processing' ? (
                            <Badge className="bg-yellow-100 text-yellow-700">Processing...</Badge>
                          ) : report.status === 'cancelled' ? (
                            <Badge className="bg-gray-300 text-gray-700">Cancelled</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-500">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {watchlistTab === 'current' ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 px-2"
                                  title="Cancel (move to Historical)"
                                  data-testid={`cancel-${report.reference_no}`}
                                  onClick={() => cancelExpectedReport(report.id)}
                                >
                                  <span className="text-xs">Cancel</span>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                                  title="Delete permanently"
                                  data-testid={`delete-${report.reference_no}`}
                                  onClick={() => deleteExpectedReport(report.id)}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                {report.status === 'cancelled' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50 h-8 px-2"
                                    title="Restore to Current"
                                    data-testid={`restore-${report.reference_no}`}
                                    onClick={() => restoreExpectedReport(report.id)}
                                  >
                                    <span className="text-xs">Restore</span>
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                                  title="Delete permanently"
                                  data-testid={`delete-${report.reference_no}`}
                                  onClick={() => deleteExpectedReport(report.id)}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">
                  {watchlistTab === 'current' ? 'No current reports in watchlist' : 'No historical reports yet'}
                </p>
              )}
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* Report Tracker Capsules — one capsule per watchlist entry (split by Current/Historical) */}
          {(() => {
            // Build capsule list from the same tab-filtered watchlist so capsules track the tab.
            const scopedWatchlist = expectedReports.filter(r =>
              watchlistTab === 'historical' ? isHistoricalEntry(r) : !isHistoricalEntry(r)
            );
            const scopedRefs = new Set(scopedWatchlist.map(r => r.reference_no));
            const uniqueRefs = [...scopedRefs];
            const scopedTrackerEntries = emailTrackerEntries.filter(e => scopedRefs.has(e.reference_no));
            const filteredEntries = selectedTrackerRef === 'all'
              ? scopedTrackerEntries
              : scopedTrackerEntries.filter(e => e.reference_no === selectedTrackerRef);
            const totalEmails = filteredEntries.length;
            const readEmails = filteredEntries.filter(e => e.status === 'read').length;
            const expiredEmails = filteredEntries.filter(e => e.status === 'expired').length;
            const pendingEmails = totalEmails - readEmails - expiredEmails;
            const totalNew = filteredEntries.reduce((sum, e) => sum + (e.new_count || 0), 0);
            const totalDuplicate = filteredEntries.reduce((sum, e) => sum + (e.duplicate_count || 0), 0);

            return (
              <>
                {uniqueRefs.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="tracker-capsules">
                    <button
                      data-testid="tracker-capsule-all"
                      onClick={() => setSelectedTrackerRef('all')}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        selectedTrackerRef === 'all'
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      All ({scopedTrackerEntries.length})
                    </button>
                    {uniqueRefs.map(ref => {
                      const refEntries = scopedTrackerEntries.filter(e => e.reference_no === ref);
                      const refRead = refEntries.filter(e => e.status === 'read').length;
                      // Prefer the DB label from the watchlist row (always present), fall back to tracker
                      const watchlistEntry = expectedReports.find(r => r.reference_no === ref);
                      const refDb = watchlistEntry?.report_type || refEntries[0]?.report_type || '';
                      return (
                        <button
                          key={ref}
                          data-testid={`tracker-capsule-${ref}`}
                          onClick={() => setSelectedTrackerRef(ref)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            selectedTrackerRef === ref
                              ? 'bg-gray-900 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {ref} <span className="opacity-60">({refDb})</span> — {refRead}/{refEntries.length} read
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Summary row for selected filter */}
                {filteredEntries.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                    <div className="bg-blue-50/70 border border-blue-100 rounded-lg px-4 py-3 text-center">
                      <p className="text-xs text-gray-500 mb-0.5">Total Emails</p>
                      <p className="text-xl font-bold text-blue-700">{totalEmails}</p>
                    </div>
                    <div className="bg-green-50/70 border border-green-100 rounded-lg px-4 py-3 text-center">
                      <p className="text-xs text-gray-500 mb-0.5">Read</p>
                      <p className="text-xl font-bold text-green-700">{readEmails}</p>
                    </div>
                    <div className="bg-amber-50/70 border border-amber-100 rounded-lg px-4 py-3 text-center">
                      <p className="text-xs text-gray-500 mb-0.5">Pending</p>
                      <p className="text-xl font-bold text-amber-700">{pendingEmails}</p>
                    </div>
                    <div className="bg-red-50/70 border border-red-100 rounded-lg px-4 py-3 text-center">
                      <p className="text-xs text-gray-500 mb-0.5">Expired</p>
                      <p className="text-xl font-bold text-red-400">{expiredEmails}</p>
                    </div>
                    <div className="bg-gray-50/70 border border-gray-200 rounded-lg px-4 py-3 text-center">
                      <p className="text-xs text-gray-500 mb-0.5">Records</p>
                      <p className="text-xl font-bold text-gray-800">{(totalNew + totalDuplicate).toLocaleString()}</p>
                      <div className="flex justify-center gap-3 mt-0.5">
                        <span className="text-[10px] text-emerald-600">{totalNew.toLocaleString()} new</span>
                        <span className="text-[10px] text-orange-500">{totalDuplicate.toLocaleString()} dup</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Report Status Tracker — shows individual emails */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" data-testid="report-status-tracker-title">
                      <Activity className="w-5 h-5" />
                      Report Status Tracker
                      {filteredEntries.length > 0 && (
                        <Badge variant="outline">{filteredEntries.length} emails</Badge>
                      )}
                      <div className="ml-auto flex gap-2">
                        <Button 
                          variant="default" 
                          size="sm" 
                          onClick={checkExpectedReports}
                          disabled={emailBrowserLoading}
                          data-testid="scan-emails-btn"
                        >
                          {emailBrowserLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Mail className="w-4 h-4 mr-2" />
                          )}
                          {emailBrowserLoading ? 'Scanning...' : 'Scan Emails'}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={fetchExpectedReports}
                          disabled={loadingExpectedReports}
                          data-testid="tracker-refresh-btn"
                        >
                          <RefreshCcw className={`w-4 h-4 mr-2 ${loadingExpectedReports ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingExpectedReports ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                        <span className="ml-2 text-gray-500">Loading tracker...</span>
                      </div>
                    ) : filteredEntries.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No emails found yet</p>
                        <p className="text-sm text-gray-400 mt-1">Click "Check Expected" to scan Gmail for matching reports</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Reference No.</TableHead>
                            <TableHead>DB</TableHead>
                            <TableHead>Email Date</TableHead>
                            <TableHead className="text-center">
                              <div className="flex flex-col items-center">
                                <span>Received</span>
                                <span className="text-xs text-gray-400 font-normal">Email found</span>
                              </div>
                            </TableHead>
                            <TableHead className="text-center">
                              <div className="flex flex-col items-center">
                                <span>Read</span>
                                <span className="text-xs text-gray-400 font-normal">Data in DB</span>
                              </div>
                            </TableHead>
                            <TableHead className="text-center">
                              <div className="flex flex-col items-center">
                                <span>Records</span>
                                <div className="flex gap-3 mt-0.5">
                                  <span className="text-[10px] text-emerald-600 font-normal">New</span>
                                  <span className="text-[10px] text-orange-500 font-normal">Duplicate</span>
                                </div>
                              </div>
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredEntries.map((entry, index) => (
                            <TableRow key={entry.email_uid || index}>
                              <TableCell className="font-mono text-sm font-semibold">
                                {entry.reference_no}
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary">{entry.report_type}</Badge>
                              </TableCell>
                              <TableCell className="text-sm">
                                {entry.email_date ? (
                                  <div>
                                    <span>{new Date(entry.email_date).toLocaleDateString('en-IN', {
                                      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric'
                                    })}</span>
                                    <span className="text-xs text-gray-400 block">
                                      {new Date(entry.email_date).toLocaleTimeString('en-IN', {
                                        timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit'
                                      })}
                                    </span>
                                  </div>
                                ) : '-'}
                              </TableCell>
                              <TableCell className="text-center">
                                {entry.received_at ? (
                                  <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                                ) : (
                                  <Clock className="w-5 h-5 text-gray-300 mx-auto" />
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {entry.status === 'read' ? (
                                  <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                                ) : entry.status === 'expired' ? (
                                  <span className="text-xs text-gray-400 font-medium">Expired</span>
                                ) : entry.status === 'empty_suspected' ? (
                                  <div className="flex flex-col items-center gap-1" data-testid={`tracker-empty-suspected-${index}`}>
                                    <AlertTriangle className="w-5 h-5 text-amber-500 mx-auto" />
                                    <span
                                      className="text-[10px] text-amber-700 max-w-[200px] truncate cursor-help font-medium"
                                      title={entry.scan_error || '0 records extracted - possible parser failure'}
                                    >
                                      ⚠ 0 records
                                    </span>
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center gap-1">
                                    <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
                                    {entry.scan_error && (
                                      <span
                                        className="text-[10px] text-red-600 max-w-[200px] truncate cursor-help"
                                        title={entry.scan_error}
                                        data-testid={`tracker-scan-error-${index}`}
                                      >
                                        ⚠ {entry.scan_error}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {entry.status === 'read' && ((entry.new_count ?? 0) > 0 || (entry.duplicate_count ?? 0) > 0) ? (
                                  <div className="flex justify-center gap-3" title={entry._counts_mirrored ? "Counts mirrored from duplicate scan of the same email" : undefined}>
                                    <span className="text-sm font-medium text-emerald-700">{(entry.new_count || 0).toLocaleString()}</span>
                                    <span className="text-sm font-medium text-orange-600">{(entry.duplicate_count || 0).toLocaleString()}</span>
                                  </div>
                                ) : entry.records_count ? (
                                  <span className="font-semibold text-sm">{entry.records_count?.toLocaleString()}</span>
                                ) : entry.status === 'empty_suspected' ? (
                                  <span
                                    className="text-xs text-amber-700 font-medium cursor-help"
                                    title="Reports always carry last-3-days data. 0 records = parser may have failed. Try 'Reprocess'."
                                    data-testid={`tracker-empty-records-${index}`}
                                  >
                                    ⚠ Retry
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </>
            );
          })()}
        </TabsContent>

        {/* BSE Client Master Tab — Runs at 3:30 AM IST via APScheduler.
            Backend: routers/bse_scraper.py. Saves to /raw-files as "BSE Client Master". */}
        <TabsContent value="bse-client-master">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Schedule</p>
                    <p className="text-lg font-semibold">Daily at 3:30 AM IST</p>
                    <p className="text-sm text-gray-400 mt-1">Logs into bsestarmf.in, OTP via Gmail</p>
                  </div>
                  <Calendar className="w-8 h-8 text-indigo-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Last run</p>
                    <p className="text-lg font-semibold text-indigo-600" data-testid="bse-cm-last-run-at">
                      {bseClientMaster?.uploaded_at
                        ? new Date(bseClientMaster.uploaded_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
                        : "—"}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      {bseClientMaster ? `${(bseClientMaster.size_bytes / 1024).toFixed(1)} KB · ${bseClientMaster.uploaded_by}` : "No run yet"}
                    </p>
                  </div>
                  <Activity className="w-8 h-8 text-indigo-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={async () => {
                      setRunningBseCM(true);
                      try {
                        const start = await api.post("/bse-scraper/run-now");
                        if (start.data?.already_running) {
                          toast.info("Scrape already in progress — polling for completion…");
                        } else {
                          toast.info("Scrape started — this takes 60-120s. Polling…");
                        }
                        // Poll job-status every 4s for up to 3 minutes
                        for (let i = 0; i < 45; i++) {
                          await new Promise((r) => setTimeout(r, 4000));
                          const s = await api.get("/bse-scraper/job-status");
                          const j = s.data?.job || {};
                          if (j.status === "completed") {
                            toast.success("BSE Client Master scrape completed", {
                              description: `Saved ${j.summary?.size_bytes ?? 0} bytes`,
                            });
                            const lr = await api.get("/bse-scraper/last-run");
                            setBseClientMaster(lr.data?.last_run || null);
                            break;
                          }
                          if (j.status === "failed") {
                            toast.error("Scrape failed", {
                              description: (j.error || "").slice(0, 300),
                            });
                            break;
                          }
                        }
                      } catch (e) {
                        toast.error("Request failed", { description: e?.response?.data?.detail || e.message });
                      } finally {
                        setRunningBseCM(false);
                      }
                    }}
                    disabled={runningBseCM}
                    data-testid="bse-cm-run-now"
                  >
                    {runningBseCM ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running (60-120s)…</>
                    ) : (
                      <><PlayCircle className="w-4 h-4 mr-2" /> Run Now</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => (window.location.href = "/raw-files")}
                    data-testid="bse-cm-open-raw-files"
                  >
                    <Download className="w-4 h-4 mr-2" /> Open in Raw Files
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>How this cron works</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1 text-gray-700">
              <p>1. APScheduler fires at 22:00 UTC (3:30 AM IST) — job id <code>bse_client_master_daily</code>.</p>
              <p>2. Playwright loads bsestarmf.in, fills Member 19941 / User 1994101 / Password (from .env).</p>
              <p>3. Waits for the OTP field, then polls Gmail IMAP (info@kinntegra.co.in) for the newest UNSEEN mail whose subject matches <code>BSE StAR MF OTP</code> for up to 120s. Extracts the 6-digit OTP.</p>
              <p>4. Navigates to <b>Admin → Admin Report → Client Master Report</b>, fills <i>Data Type = Creation Date</i>, <i>From = 01/01/2018</i>, <i>To = today</i>, <i>Format = TXT</i>.</p>
              <p>5. Captures the download and writes it to GridFS via <code>/api/raw-files/upload</code> with canonical name <b>BSE Client Master</b>. Previous copy is marked superseded (audit-safe).</p>
              <p>6. Every step is logged into the raw_files metadata doc's <code>scraper_summary.steps[]</code> — grep the log to adjust selectors if the portal DOM shifts.</p>
              <p className="text-amber-700 pt-2">
                First live run is a smoke test — selectors are best-guesses with 2-3 fallbacks each. If any step fails, check <code>db.raw_files.findOne(&#123;name:"BSE Client Master"&#125;).scraper_summary.steps</code>.
              </p>
            </CardContent>
          </Card>

          {bseClientMaster?.scraper_summary?.steps && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Last run — step trace</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <pre className="text-xs bg-gray-50 p-3 rounded" data-testid="bse-cm-step-trace">
                  {JSON.stringify(bseClientMaster.scraper_summary.steps, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Cronjobs;
